/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useId, useEffect, useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Button, Text, Link, Types, Messages, CheckBox, InputTextField, RichText } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { sortErrorObject } from '@marriott/shared/mi-helper-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { useSession } from '@marriott/mi-headless-utils';
import { PageContext } from '../../../modules/context/PageContext';
import {
  constants,
  CREATE_ACCOUNT_CLICK_VAL,
  CHINESE_MOBILE_CODE,
  CHINESE_MOBILE_LENGTH,
  NUMBERS_REGEX,
  ONE_CLICK_JOIN_ERROR,
  CHINESE_SOURCE_LANG,
  ENGLISH_SOURCE_LANG,
  BANNER_MSGS_KEYS,
  generateOneTimePwrdUrl,
  DUPLICATE_PHONE_ERROR_KEY,
  ENROLL_PATTERN_EXPERIENCES,
  nameLimit20,
  nameLimit25,
  nameErrorMessageCode,
  ENROLLMENT_VARIATION_PHONE_REQUIRED,
  ENROLLMENT_VARIATION_PHONE_AND_MFA_REQUIRED,
  ENROLLMENT_VARIATION_PHONE_OPTIONAL,
  EMAIL_DUPLICATE_ERROR,
  EMAIL_UNIQUENESS_ERROR,
  PHONE_UNIQUENESS_ERROR_KEY,
  ENROLLMENT_FAIL,
  PHONE_NUMBER_ERROR,
  CREATE_ACCOUNT_API_ERROR,
  MOBILE_PHONE_REQUIRED_ERROR_KEY,
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  VERIFICATION_CODE_ERROR,
  DEFAULT_ENROLLMENT_VARIATION,
  TRACKING_CONSTANTS,
} from '../../../modules/utils/constants/constants';
import {
  checkChinaLocale,
  checkForValidZipCode,
  emailRegex,
  generateApolloClientHeaders,
  getCountryAndLangCode,
  isChinese,
  randDecrpytAPI,
  scrollIntoViewClass,
  shouldSwapName,
  nameFieldRegex,
  nameRegExpValidation,
  canUseDOM,
  getCurrentUrlParams,
  logger,
  mergeMultipleParams,
  addSubDirectoryPrefixForNonEaa,
  calculateOtpTimeLeft,
  checkKOLocale,
  transformAnchorAttributes,
  decodeBase64,
  EVENT_NAME,
  updateDataLayerProperties,
  updateDataLayerAttributes,
  DATALAYER_PAGE_EVENT,
  isValidValue,
  extractRequestId,
  formatServerTimestamp,
  buildEnrollmentEventDetails,
  handleFallback,
  enrollACDLkeyGenerator,
  getCustomClickTrackingPageName,
} from '../../../modules/utils';
import { phoenixAccountTransliterate } from '../../../modules/graph';
import { useCreateAccountStore } from '../../../modules/store/createAccountStore';
import { useAccountPersistentStore } from '../../../modules/store/accountPersistentStore';
import { useBannerMessagesStore } from '../../../modules/store/bannerMessagesStore';
import { PhoneNumberField } from '../../../molecules/PhoneNumberField';
import { Dropdown } from '../../../molecules/Dropdown';
import { PasswordInput } from '../../../molecules/PasswordInput';
import { replaceText } from '../../../modules/utils/promotionHelper';
import { DecodedUXLMigrationStatus } from '../../../modules/utils/commonInterface';
import { StyledInputTextFieldNumber } from '../../../molecules/PasswordInput/PasswordInput.styles';
import {
  CreateAccountFormProps,
  CountryOption,
  CountryList,
  PrefillResponse,
  CreateAccountErrors,
} from './CreateAccountForm.types';
import { StyledCreateAccountForm } from './CreateAccountForm.styles';
import { CreateAccountOtpTimer } from './CreateAccountOtpTimer/CreateAccountOtpTimer';
import { CreateAccountConfirmIdentity } from './CreateAccountConfirmIdentity/CreateAccountConfirmIdentity';
import { partnerPortalListType } from '../CreateAccount.types';

const { log } = logger({})('CreateAccountForm');

// Use named rather than default exports.
export const CreateAccountForm: FC<CreateAccountFormProps> = models => {
  const {
    nameInlineErrorMsg,
    pwrdCharCheck,
    pwrdLowerCaseCheck,
    pwrdUpperCaseCheck,
    pwrdSpecialCharCheck,
    countries,
    firstNameLabel,
    lastNameLabel,
    firstNameLabelChinese,
    lastNameLabelChinese,
    zipPostalCodeLabel,
    countryRegionLabel,
    emailLabel,
    mobilePhoneLabel,
    sendVerificationLabel,
    sendVerificationDescription,
    verificationCodeLabel,
    rememberMeLabel,
    confirmPwrdLabel,
    pwrdLabel,
    createPwrdLabel,
    firstNameError,
    firstNameBannerError,
    lastNameError,
    lastNameBannerError,
    zipCodeError,
    emailErrorLabel,
    alreadyAMemberText,
    activateOnlineAccountText,
    activateOnlineAccountPath,
    pwrdErrorLabel,
    confirmAndNewPwrdMismatchMsg,
    errorMessageHeader,
    mobilePhoneError,
    verificationCodeRequired,
    mobilePhoneRequired,
    mobilePhoneDigitsRequired,
    pinyinEnglishSurname,
    pinyinEnglishName,
    chinaSurNameError,
    chinaNameError,
    prohibitedCountryErrMsg,
    emailDuplicateErrMsg,
    emailDuplicateError,
    inlineMessageForName,
    zipInlineError,
    emailInvalidError,
    optionalLabel,
    zipCodeInvalidError,
    eligibleCountries,
    isValidClientId,
    partnerPortalObject,
    isDTT,
    eaa,
    inlineErrorChineseName,
    incorrectPinyinEnglishLastNameFormat,
    incorrectPinyinEnglishFirstNameFormat,
    incorrectChineseLastNameFormat,
    incorrectChineseFirstNameFormat,
    existingMobPhoneNumWarning,
    existingPhoneNumberWarning,
    countryCodeLabel,
    mobilePhoneInvalidError,
    mobilePhoneEmptyError,
    verificationCodeInlineError,
    inlineLabel = true,
    pwrdHeader,
    memberSignInLabel,
    pwrdCharLabel,
    pwrdLowerCaseLabel,
    pwrdSpecialCharLabel,
    pwrdUpperCaseLabel,
    inlineErrorNameCharLimit,
    firstNameCharLimitErrorMsg,
    lastNameCharLimitErrorMsg,
    mobilePhoneFieldLabel,
    inlineErrorMsgMobNumInvalid,
    inlineErrorMsgMobNumMandatory,
    errorBannerMsgMobNumInvalid,
    phoneAlreadyAssociatedError,
  } = models;
  const {
    formData,
    setFormData,
    errorKey,
    setErrorKey,
    setTransactionDetails,
    setTripDetails,
    setIsDTTforRandQueryParam,
    setJoinNowBtnClicked,
    joinNowBtnClicked,
    errorsList,
    setErrorsList,
    remembermeCheckBox,
    consentErrors,
    setRemembermeCheckBox,
    setFormValidation,
    goBackUrl,
    isValidPartnerPortal,
    prefillObj,
    setPrefillObj,
    resetTimer,
    setResetTimer,
    showSendCode,
    setShowSendCode,
    selectedOption,
    setSelectedOption,
    setExperienceEnrollmentVariationFromDom,
    experienceEnrollmentVariationFromDom,
  } = useCreateAccountStore();

  const { isACDLenabled, updateDatalayer } = useSession();

  const passwordCondArr = [
    {
      condition: pwrdCharCheck,
      isValid: false,
    },
    {
      condition: pwrdUpperCaseCheck,
      isValid: false,
    },
    {
      condition: pwrdLowerCaseCheck,
      isValid: false,
    },
    {
      condition: pwrdSpecialCharCheck,
      isValid: false,
    },
  ];
  const passwordCondLabelArr = [
    {
      condition: pwrdCharLabel || '',
      isValid: false,
    },
    {
      condition: pwrdUpperCaseLabel || '',
      isValid: false,
    },
    {
      condition: pwrdLowerCaseLabel || '',
      isValid: false,
    },
    {
      condition: pwrdSpecialCharLabel || '',
      isValid: false,
    },
  ];

  const pageContext = useContext(PageContext);
  const prefillResponse: PrefillResponse = pageContext?.enrollPrefillResponse;
  const isPatternFour = prefillResponse?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_FOUR;
  const isPatternTwo = prefillResponse?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_TWO;
  const isPatternThree = prefillResponse?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_THREE;
  const { isMobileAuthFeatureEnabled, isNonChinaMobileEnabled, currentLocale } = pageContext;
  const { countryCode } = getCountryAndLangCode(currentLocale);
  const localeCountryCode = countryCode?.toUpperCase() || constants.USA_COUNTRY_CODE;
  const localeBasedNameSwapConfig = pageContext?.localeBasedNameSwapConfig;
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const featureFlags = pageContext?.featureFlags;
  const { PROHIBITED_COUNTRIES_CODES: prohibitedCountries, PATTERN_THREE_COUNTRIES: patternThreeCountries } =
    clientEnvVars;
  const patternThreeCountriesList: Array<string> = patternThreeCountries?.split(',') || [];
  const prohibitedCountriesArr: Array<string> = prohibitedCountries?.split(',') || [];
  const { randParameter, isOCJFlow, bannerMsgs } = useBannerMessagesStore(state => state);
  const { isDTTFlag, setIsDTTFlag } = useAccountPersistentStore(state => state);
  const isChinaLocale = checkChinaLocale(currentLocale || '');
  const isKoreanLocale = checkKOLocale(currentLocale || '');
  const router = useRouter();
  const inputId = useId();
  const [queryParams, setQueryParams] = useState('');
  const [passwordType, setPasswordType] = useState<'text' | 'password'>('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState<'text' | 'password'>('password');
  const [passwordConditions, setPasswordConditions] = useState(pwrdHeader ? passwordCondLabelArr : passwordCondArr);
  const [disableField, setDisableField] = useState(isChinaLocale);
  const [mobileField, setMobileField] = useState(false);
  const [mobileFieldOptional, setMobileFieldOptional] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [showZipcodeField, setShowZipcodeField] = useState(false);
  const [reverseNamefield, setReverseNamefield] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState<CountryOption>();
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [disablePhoneCountry, setDisablePhoneCountry] = useState<boolean>(false);
  const [disablePhoneSendCode, setDisablePhoneSendCode] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const otpMapRef = useRef<{ [phone: string]: number }>({});
  const isValidChangePassword = !passwordValid;
  const { IS_LOCAL_DEV } = process.env;
  const [domRendered, setDomRendered] = useState(false);
  const isTargetBasedDataLayerEnabled = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_TARGET_ENABLED];
  const [experienceEnrollmentVariation, setexperienceEnrollmentVariation] = useState('');

  const isPhoneNumberRequired = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_REQUIRED;
  const isPhoneNumberAndMFARequired = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_AND_MFA_REQUIRED;
  const isPhoneNumberOptional = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_OPTIONAL;
  const hasPhoneNumberField = isPhoneNumberRequired || isPhoneNumberAndMFARequired || isPhoneNumberOptional;
  const mobilePhoneLabelText = handleFallback(mobilePhoneFieldLabel, mobilePhoneLabel);

  const [errors, setErrors] = useState<CreateAccountErrors>({
    firstNameError: '',
    lastNameError: '',
    errorFirstName: '',
    errorLastName: '',
    zipCodeError: '',
    emailErrorLabel: '',
    pwrdErrorLabel: '',
    confirmAndNewPwrdMismatchMsg: '',
    verificationCodeRequired: '',
    mobilePhoneRequired: '',
    mobilePhoneDigitsRequired: '',
    mobilePhoneError: '',
    chinaSurNameError: '',
    chinaNameError: '',
    prohibitedCountryErrMsg: '',
    zipCodeInvalidError: '',
    emailInvalidError: '',
    usPostalCodeInvalidError: '',
    canadaPostalCodeInvalidError: '',
    uxlFailureErrorMessage: '',
    existingAccountInlineErrorMessage: '',
    incorrectChineseLastNameError: '',
    incorrectChineseFirstNameError: '',
    incorrectVerificationCode: '',
    expiredTransactionCode: '',
    phoneAlreadyAssociatedError: '',
    transactionIdExpiredError: '',
    invalidOneTimePasswordError: '',
    otpRequired: '',
    otpIncorrectErrorMessage: '',
    mobilePhoneInvalidError: '',
    mobilePhoneEmptyError: '',
    verificationCodeInlineError: '',
    errorPinyinEnglishSurNameInline: '',
    errorpinyinEnglishNameInline: '',
    mobileNumberErrorMessage: '',
  });
  const [disableSecondHalf, setDisableSecondHalf] = useState(isPhoneNumberAndMFARequired);
  const [chineseCharInput, setChineseCharInput] = useState<{
    firstNameInlineErrorFlag: boolean;
    lastNameInlineErrorFlag: boolean;
  }>({
    firstNameInlineErrorFlag: false,
    lastNameInlineErrorFlag: false,
  });

  // skipEnrollmentEventDetails: true for API errors (enrollmentEventDetails comes from API response headers)
  const updatePhoneNumberAndOtpErrorInDatalayer = (
    skipEnrollmentEventDetails?: boolean,
    isVerificationCode?: boolean,
    isPhoneAlreadyAssociated?: boolean
  ) => {
    const requestId = canUseDOM
      ? (window as unknown as { dataLayer?: { request_id?: string } })?.dataLayer?.request_id
      : undefined;

    const serverTimestamp = pageContext?.serverTimestamp;

    let enrollmentEventDetails: string | undefined;
    let enrollmentEventDetailsUser: Record<string, any> = {};

    if (!skipEnrollmentEventDetails && isValidValue(experienceEnrollmentVariation) && requestId) {
      const extractedRequestId = extractRequestId(requestId);
      const formattedTimestamp = formatServerTimestamp(serverTimestamp);
      const requestIdWithTimestamp = formattedTimestamp
        ? `${extractedRequestId}#${formattedTimestamp}`
        : extractedRequestId;

      enrollmentEventDetails = buildEnrollmentEventDetails([
        `Pattern ${experienceEnrollmentVariation as string}`,
        ENROLLMENT_FAIL,
        isVerificationCode
          ? VERIFICATION_CODE_ERROR
          : isPhoneAlreadyAssociated
          ? EVENT_NAME.PHONE_ALREADY_ASSOCIATED_ERROR
          : PHONE_NUMBER_ERROR,
        requestIdWithTimestamp,
      ]);

      // Use enrollACDLkeyGenerator to parse enrollmentEventDetails
      // and extract USER object for analytics
      const enrollACDLParsed = enrollACDLkeyGenerator(enrollmentEventDetails);
      if (enrollACDLParsed && typeof enrollACDLParsed === 'object') {
        enrollmentEventDetailsUser = enrollACDLParsed.USER || {};
      }
    }
    const dataLayerProps: {
      phone_number_error: boolean;
    } = {
      phone_number_error: true,
      ...(enrollmentEventDetails && !isACDLenabled && { enrollmentEventDetails }),
      ...(Object.keys(enrollmentEventDetailsUser).length > 0 && { USER: enrollmentEventDetailsUser }),
    };
    const dataLayerEvent = isACDLenabled
      ? {
          eventName: enrollmentEventDetails ? EVENT_NAME.ACCOUNTCREATE : EVENT_NAME.PHONE_NUMBER_ERROR,
          update: updateDatalayer,
        }
      : undefined;
    updateDataLayerProperties(
      dataLayerProps,
      isVerificationCode
        ? DATALAYER_PAGE_EVENT.VERIFICATION_CODE_ERROR
        : isPhoneAlreadyAssociated
        ? DATALAYER_PAGE_EVENT.PHONE_ALREADY_ASSOCIATED_ERROR
        : DATALAYER_PAGE_EVENT.PHONE_NUMBER_ERROR,
      dataLayerEvent
    );
  };

  const resetPwrd = () => {
    setFormData({ pwrd: '', confirmPwrd: '' });
    setPasswordValid(false);
  };

  const randDecryptionAPI = async (randQueryParameter: string) => {
    let translatedFirstName, translatedLastName;

    if (randParameter || randQueryParameter) {
      try {
        const response = await randDecrpytAPI(
          isOCJFlow ? randParameter : randQueryParameter,
          isOCJFlow ? isDTTFlag : randQueryParameter ? true : isDTT
        );
        if (response?.status === 200) {
          if (isChinaLocale) {
            if (response?.data?.firstName) {
              const firstNameResponse = await englishTranslateFromChinese(response?.data?.firstName as string);
              translatedFirstName = firstNameResponse?.data?.transliterate?.transliteratedText?.[0] as string;
              nameRegExpValidation(
                firstNameResponse?.data?.transliterate?.transliteratedText?.[0],
                'pinyinEnglishName',
                'errorpinyinEnglishNameInline',
                setErrors,
                errors,
                nameInlineErrorMsg
              );
            }

            if (response?.data?.lastName) {
              const lastNameResponse = await englishTranslateFromChinese(response?.data?.lastName as string);
              translatedLastName = lastNameResponse?.data?.transliterate?.transliteratedText?.[0] as string;
              nameRegExpValidation(
                lastNameResponse?.data?.transliterate?.transliteratedText?.[0],
                'pinyinEnglishSurname',
                'errorPinyinEnglishSurNameInline',
                setErrors,
                errors,
                nameInlineErrorMsg
              );
            }
          }
          const firstNameField = isChinaLocale ? 'pinyinEnglishName' : 'firstName';
          const lastNameField = isChinaLocale ? 'pinyinEnglishSurname' : 'lastName';

          setFormData({
            ...formData,
            [firstNameField]: response?.data?.firstName,
            [lastNameField]: response?.data?.lastName,
            zipcode: response?.data?.postalCode,
            email: response?.data?.email,
            ...(isChinaLocale && {
              pinyinEnglishName: translatedFirstName || prefillResponse?.firstName,
              pinyinEnglishSurname: translatedLastName || prefillResponse?.lastName,
            }),
          });

          //Check for first and last name length for rand data
          const nameErrors = [];
          const firstName = response?.data?.firstName;
          if (!!firstName && firstName?.trim().length > nameLimit20) {
            nameErrors.push(nameErrorMessageCode.firstNameCharLimitErrorMsg);
          }
          const lastName = response?.data?.lastName;
          if (!!lastName && lastName?.trim().length > nameLimit25) {
            nameErrors.push(nameErrorMessageCode.lastNameCharLimitErrorMsg);
          }

          setErrorKey(nameErrors);
          const randCountryCode = response?.data?.country?.toUpperCase();
          const isZipCodeVisibleCountry =
            (randCountryCode && randCountryCode === constants.CANADA_COUNTRY_CODE) ||
            randCountryCode === constants.USA_COUNTRY_CODE;
          handleCountryNameChange(
            getCountryDetailByCountryCode(response?.data?.country),
            false,
            false,
            false,
            isZipCodeVisibleCountry ? response?.data?.postalCode : ''
          );
          setTripDetails(response?.data?.tripDetails);
          if (randQueryParameter) {
            const decodedMigrationStatus: DecodedUXLMigrationStatus = JSON.parse(
              decodeBase64(response?.data?.tripDetails?.uxlMigrationStatus) || ''
            );
            setIsDTTforRandQueryParam(decodedMigrationStatus?.isDtt);
          }
          if (isOCJFlow || randQueryParameter) {
            checkProhibitedCountries(response?.data?.country);
          }
        }
      } catch (error) {
        log.debug('Error fetching data:', error);
      }
    }
  };

  useEffect(() => {
    const getEnrollmentVariationFromForm = () => {
      const componentVariation = canUseDOM
        ? new URLSearchParams(getCurrentUrlParams() || '').get('componentVariation')
        : null;
      const attributeValue = canUseDOM
        ? document.querySelector('[data-testid="create-account-form"]')?.getAttribute('data-variation')
        : null;
      const variationValue = componentVariation?.trim() || attributeValue?.trim();

      if (!variationValue) {
        return false;
      }

      setExperienceEnrollmentVariationFromDom(variationValue || DEFAULT_ENROLLMENT_VARIATION);
      setexperienceEnrollmentVariation(
        isTargetBasedDataLayerEnabled
          ? variationValue || DEFAULT_ENROLLMENT_VARIATION
          : componentVariation?.trim() || featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS]
      );
      return true;
    };

    // Check if form already exists
    if (getEnrollmentVariationFromForm()) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (getEnrollmentVariationFromForm()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const timeoutId = setTimeout(() => {
      observer.disconnect();
    }, 10000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [isTargetBasedDataLayerEnabled]);

  // Update data layer with experienceEnrollmentVariationFromDom when Target provides the value from DOM
  useEffect(() => {
    if (!canUseDOM || !isTargetBasedDataLayerEnabled || !isValidValue(experienceEnrollmentVariationFromDom)) {
      return;
    }

    const requestId = (window as unknown as { dataLayer?: { request_id?: string } })?.dataLayer?.request_id;
    const currentDataLayer = (window as unknown as { dataLayer?: Record<string, unknown> })?.dataLayer;
    const mvpOffersData =
      typeof (window as unknown as { mvpOffers?: unknown }).mvpOffers !== 'undefined'
        ? JSON.stringify((window as unknown as { mvpOffers: unknown }).mvpOffers)
        : '{}';

    if (currentDataLayer && requestId) {
      const updatedDataLayer = updateDataLayerAttributes(
        isDTT,
        featureFlags,
        requestId,
        pageContext?.serverTimestamp,
        { data: [currentDataLayer], mvpOffersData },
        experienceEnrollmentVariationFromDom
      );

      if (updatedDataLayer?.data?.[0]) {
        Object.assign(currentDataLayer, updatedDataLayer.data[0]);
      }
    }
  }, [
    experienceEnrollmentVariationFromDom,
    isTargetBasedDataLayerEnabled,
    isDTT,
    featureFlags,
    pageContext?.serverTimestamp,
  ]);

  // to run rand api once when isCheckedIn modal is up
  // to check if clientid is valid for sweepstake variation
  useEffect(() => {
    setDomRendered(true);
    const urlQueryParams = canUseDOM ? getCurrentUrlParams()?.replace(/&amp;/g, '&') : '';

    const params = new URLSearchParams(urlQueryParams);
    params.delete('prefill_id');
    const mergedParams = mergeMultipleParams(params?.toString(), prefillResponse?.queryParams);
    setQueryParams(mergedParams);
    checkLocaleNameSwap();
    populatePrefillData(urlQueryParams);

    return () => {
      setIsDTTFlag(false);
    };
  }, []);

  useEffect(() => {
    const urlQueryParams = canUseDOM ? getCurrentUrlParams()?.replace(/&amp;/g, '&') : '';
    const param = new URLSearchParams(urlQueryParams);
    const randQueryParameter = param?.get('rand') ?? ''; // rand parameter
    if (randParameter || randQueryParameter) {
      randDecryptionAPI(randQueryParameter);
    }
  }, [randParameter]);

  useEffect(() => {
    if (bannerMsgs?.key !== BANNER_MSGS_KEYS.NEW_MEMBER_NUMBER_MSG) {
      sessionStorage.removeItem('bannerMessagesState');
    }
  }, [bannerMsgs?.key]);

  //clear verfication field if there is an error
  useEffect(() => {
    //adding showSendCode check to clear verification feild when there is change in mobile or email
    if (resetTimer || !showSendCode) {
      setFormData({ verificationCode: '' });
    }
  }, [resetTimer, showSendCode]);

  // sweepstake phone dropdown to have countries with country code +1
  const updatedSweepstakeCountriesDialCodes = useMemo(() => {
    return eligibleCountries?.map(eligibleCountry => ({
      ...eligibleCountry,
      ...(countries as CountryList[])?.find(country => country?.id === eligibleCountry?.id),
    }));
  }, []);

  // used to map country code from rand response
  const getCountryDetailByCountryCode = (countryCode: string) => {
    const countryDetail = (isValidClientId ? updatedSweepstakeCountriesDialCodes : countries)?.find(
      country => country.id === countryCode
    );
    return countryDetail;
  };

  const updatedCountriesDialCodes = (isValidClientId ? updatedSweepstakeCountriesDialCodes : countries)?.map(
    (country: { id?: string; countryDialCode?: string; value?: string }) => ({
      id: country?.id || '',
      name: country?.value || '',
      value: country?.countryDialCode || '',
    })
  );

  const countryDialCode = updatedCountriesDialCodes?.find(
    (d: any) => d.id?.toUpperCase() === countryCode?.toUpperCase()
  )?.value;

  const [selectedCountryCode, setSelectedCountryCode] = useState(countryDialCode);
  const hasOtpError =
    !!errors?.transactionIdExpiredError ||
    !!errors?.invalidOneTimePasswordError ||
    !!errors?.otpIncorrectErrorMessage ||
    !!errors?.otpRequired;

  const isChinesePhone = formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE;
  const isChinesePhoneValid = formData?.mobilePhone?.length === CHINESE_MOBILE_LENGTH;
  const isValidPhoneNo = formData?.mobilePhone?.length >= 7 && formData?.mobilePhone?.length <= 16;

  const partnerExperienceTwo = () => {
    setDisableField(false);
    setMobileField(true);
    setMobileFieldOptional(true);
    if (prefillResponse?.phone?.number && !formData?.mobilePhone) {
      setFormData({ mobilePhone: prefillResponse?.phone?.number });
    }
  };

  const partnerExperienceFour = (countryId?: string, isPrefill?: boolean) => {
    if (countryId?.toUpperCase() === constants.CHINA_COUNTRY_CODE) {
      // adding back phone number if prefill have phone number and user switches between coutries
      if (
        prefillResponse?.phone?.number &&
        prefillResponse?.phone?.countryCode === countryId &&
        !formData?.mobilePhone
      ) {
        setFormData({ mobilePhone: prefillResponse?.phone?.number });
      }
      if (
        !isPrefill ||
        (isPrefill &&
          (!prefillResponse?.phone?.countryCode ||
            prefillResponse?.phone?.countryCode?.toUpperCase() === constants.CHINA_COUNTRY_CODE))
      ) {
        setDisableField(true);
        setMobileField(true);
        setMobileFieldOptional(false);
      } else {
        setDisableField(false);
        setMobileField(true);
        setMobileFieldOptional(true);
      }
    } else if (countryId?.toUpperCase() !== constants.CHINA_COUNTRY_CODE) {
      setDisableField(false);
      setMobileField(false);
      setMobileFieldOptional(true);
      setFormData({ mobilePhone: '' });
    }
  };

  const partnerExperienceThree = (countryId?: string) => {
    //disable phone country code for USA and Canadas
    if (countryId && patternThreeCountriesList?.includes(countryId)) {
      setDisablePhoneCountry(true);
    } else {
      setDisablePhoneCountry(false);
    }
    setDisableField(true);
    setMobileField(true);
    setMobileFieldOptional(false);
  };

  const validatePartnerExperience = (countryId?: string, pattern?: number, isPrefill?: boolean) => {
    switch (pattern) {
      case ENROLL_PATTERN_EXPERIENCES.PATTERN_TWO:
        partnerExperienceTwo();
        break;
      case ENROLL_PATTERN_EXPERIENCES.PATTERN_FOUR:
        partnerExperienceFour(countryId, isPrefill);
        break;
      case ENROLL_PATTERN_EXPERIENCES.PATTERN_THREE:
        partnerExperienceThree(countryId);
        break;
      default:
        return;
    }
  };

  const phoneNumberValidation = (countryId?: string, isPrefill?: boolean) => {
    if (isPhoneNumberRequired || isPhoneNumberAndMFARequired) {
      setMobileField(true);
      setMobileFieldOptional(false);
    } else if (eaa) {
      validatePartnerExperience(countryId, prefillResponse?.partnerExperience, isPrefill);
    } else if (isChinaLocale && countryId?.toUpperCase() === constants.CHINA_COUNTRY_CODE) {
      setDisableField(true);
      setMobileField(true);
    } else if (
      (isValidClientId && !isChinaLocale) ||
      countryId?.toUpperCase() === constants.CHINA_COUNTRY_CODE ||
      isPhoneNumberOptional
    ) {
      setMobileField(true);
      setMobileFieldOptional(true);
    } else {
      setDisableField(false);
      setMobileField(false);
      setMobileFieldOptional(false);
    }
  };

  useEffect(() => {
    if (!isValidValue(experienceEnrollmentVariation)) {
      return;
    }

    phoneNumberValidation(formData?.country?.id);
    setDisableSecondHalf(isPhoneNumberAndMFARequired);
  }, [experienceEnrollmentVariation]);

  const updateDefaultCountryCode = () => {
    const defaultCountryObj = (isValidClientId ? updatedSweepstakeCountriesDialCodes : countries)?.find(
      cc => cc.id === localeCountryCode
    );

    setDefaultCountry(defaultCountryObj);
    setFormData({
      country: defaultCountryObj,
      mobilePhoneCountryId: {
        id: defaultCountryObj?.id || '',
        name: defaultCountryObj?.value || '',
        value: defaultCountryObj?.countryDialCode || '',
      },
    });
    phoneNumberValidation(defaultCountryObj?.id);
  };

  const checkLocaleNameSwap = () => {
    const localeSwapCheck = shouldSwapName(localeBasedNameSwapConfig, localeCountryCode);

    if (localeCountryCode === constants.CANADA_COUNTRY_CODE || localeCountryCode === constants.USA_COUNTRY_CODE) {
      setShowZipcodeField(true);
    }

    if (localeSwapCheck) {
      setReverseNamefield(true);
    } else {
      setReverseNamefield(false);
    }
  };

  const populatePrefillData = async (urlQueryParams: string) => {
    const param = new URLSearchParams(urlQueryParams);
    const clientID = param?.get('client_id') ?? '';
    //update default formdata based on prefill response
    if (eaa && prefillResponse) {
      let translatedFirstName, translatedLastName;
      let userPhoneNumber, userPhoneCountryCode;

      if (prefillResponse?.localizedFirstName && !prefillResponse?.firstName) {
        const firstNameResponse = await englishTranslateFromChinese(prefillResponse?.localizedFirstName as string);
        translatedFirstName = firstNameResponse?.data?.transliterate?.transliteratedText?.[0] as string;
        nameRegExpValidation(
          firstNameResponse?.data?.transliterate?.transliteratedText?.[0],
          'pinyinEnglishName',
          'errorpinyinEnglishNameInline',
          setErrors,
          errors,
          nameInlineErrorMsg
        );
      }

      if (prefillResponse?.localizedLastName && !prefillResponse?.lastName) {
        const lastNameResponse = await englishTranslateFromChinese(prefillResponse?.localizedLastName as string);
        translatedLastName = lastNameResponse?.data?.transliterate?.transliteratedText?.[0] as string;
        nameRegExpValidation(
          lastNameResponse?.data?.transliterate?.transliteratedText?.[0],
          'pinyinEnglishSurname',
          'errorPinyinEnglishSurNameInline',
          setErrors,
          errors,
          nameInlineErrorMsg
        );
      }

      const userCountryCode = prefillResponse?.countryCode?.toUpperCase() || localeCountryCode;
      const userCountryDetails = getCountryDetailByCountryCode(userCountryCode);
      const userPhoneDialCode = prefillResponse?.phone?.countryCode || userCountryCode;
      const userPhoneCountryDialcode = updatedCountriesDialCodes?.find(country => userPhoneDialCode === country.id);
      const isPatternThreeSupportedCountry = patternThreeCountriesList?.includes(userCountryCode);

      const isPhoneNumberHidden = isPatternFour && userCountryCode !== constants.CHINA_COUNTRY_CODE;

      if (
        prefillResponse?.phone?.number &&
        userPhoneCountryDialcode &&
        ((!isPhoneNumberHidden && !isPatternThree) ||
          (isPatternThree &&
            (!isPatternThreeSupportedCountry ||
              (isPatternThreeSupportedCountry && patternThreeCountriesList?.includes(userPhoneCountryDialcode?.id)))))
        // for pattern 3, we are not prefill mobile and mobile country code if preill returns non US/Canada phone code for USA/Canada countries.
        // for non US/Canada countries will prefill phone details
      ) {
        userPhoneNumber = prefillResponse?.phone?.number;
        userPhoneCountryCode = userPhoneCountryDialcode;
      }

      //disable phone send code radio option if preill has non US/Canada country and non +1 phone code
      if (
        isPatternThree &&
        userPhoneCountryDialcode &&
        !isPatternThreeSupportedCountry &&
        userPhoneCountryDialcode?.value !== constants?.USA_COUNTRY_DIAL_CODE
      ) {
        setDisablePhoneSendCode(true);
      }
      //Check for first and last name length for prefill data
      const nameErrors = [];
      const firstName = isChinaLocale ? translatedFirstName || prefillResponse?.firstName : prefillResponse?.firstName;
      if (!!firstName && firstName?.trim().length > nameLimit20) {
        nameErrors.push(nameErrorMessageCode.firstNameCharLimitErrorMsg);
      }
      const lastName = isChinaLocale ? translatedLastName || prefillResponse?.lastName : prefillResponse?.lastName;
      if (!!lastName && lastName?.trim().length > nameLimit25) {
        nameErrors.push(nameErrorMessageCode.lastNameCharLimitErrorMsg);
      }
      setErrorKey(nameErrors);

      setFormData({
        ...formData,
        firstName: isChinaLocale ? prefillResponse?.localizedFirstName : prefillResponse?.firstName || '',
        lastName: isChinaLocale ? prefillResponse?.localizedLastName : prefillResponse?.lastName || '',
        email: prefillResponse?.email || '',
        zipcode: prefillResponse?.postalCode || '',
        mobilePhone: userPhoneNumber || '',
        ...(userPhoneCountryCode && {
          mobilePhoneCountryId: userPhoneCountryCode,
        }),
        ...(isChinaLocale && {
          pinyinEnglishName: translatedFirstName || prefillResponse?.firstName,
          pinyinEnglishSurname: translatedLastName || prefillResponse?.lastName,
        }),
      });
      setPrefillObj?.({
        enrollmentSourceCode: prefillResponse?.enrollmentSourceCode,
        partnerUrl: prefillResponse?.redirectUri,
        queryParams: `${prefillResponse?.queryParams}&locale=${currentLocale}&clientId=${clientID}`,
        partnerExperience: prefillResponse?.partnerExperience,
      });
      handleCountryNameChange(userCountryDetails, isPhoneNumberHidden || !!userPhoneNumber, true, true);
      handleDefaultOtpOption(userCountryDetails?.id, userPhoneCountryCode?.value, userPhoneNumber);
      setSelectedCountryCode(userPhoneCountryDialcode?.value || userCountryDetails?.countryDialCode);
    } else {
      updateDefaultCountryCode();
    }
  };

  const removeErrorKey = (targetKey: string) => {
    if (errorKey?.includes(targetKey)) {
      setErrorKey(errorKey?.filter(err => err !== targetKey));
    }
  };

  useEffect(() => {
    const isPasswordValid = passwordConditions.reduce((acc, curr) => acc && curr.isValid, true);
    setPasswordValid(isPasswordValid);
  }, [passwordConditions]);

  const handleDefaultOtpOption = (countryId?: string, phoneCountryId?: string, phoneNumber?: string) => {
    const isCountryCanadaOrUSA =
      countryId?.toUpperCase() === constants.USA_COUNTRY_CODE ||
      countryId?.toUpperCase() === constants.CANADA_COUNTRY_CODE;
    const isUSAMobileCountryCode = phoneCountryId === constants?.USA_COUNTRY_DIAL_CODE;
    if (isPatternThree && !!phoneNumber && isUSAMobileCountryCode && isCountryCanadaOrUSA) {
      setSelectedOption(constants.PHONE);
    }
  };

  //updtOnlyCountry - this attribute is used while decryption of eaa prefill
  const handleCountryNameChange = (
    option: any,
    updtOnlyCountry?: boolean,
    isPrefill?: boolean,
    skipDefaultOtpUpdate?: boolean,
    zipcode?: string
  ) => {
    if (
      option.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE ||
      option.id?.toUpperCase() === constants.USA_COUNTRY_CODE
    ) {
      !skipDefaultOtpUpdate && handleDefaultOtpOption(option.id, option?.countryDialCode, formData?.mobilePhone);
      setFormData({ zipcode: zipcode || prefillResponse?.postalCode || '' });
      setShowZipcodeField(true);
    } else {
      setFormData({ zipcode: '' });
      setShowZipcodeField(false);
    }

    !updtOnlyCountry &&
      fetchMobileNumber({
        countryCode: option?.countryDialCode,
        mobileNumber: formData?.mobilePhone,
        id: option?.id,
      });
    phoneNumberValidation(option?.id, isPrefill);
    if (isPhoneNumberRequired || isPhoneNumberAndMFARequired) {
      const phoneCountry = updatedCountriesDialCodes?.find(d => d.id === option?.id);
      setFormData({
        country: { ...option },
        ...(phoneCountry && { mobilePhoneCountryId: phoneCountry }),
      });
    } else {
      setFormData({ country: { ...option } });
    }
    !zipcode && resetForm('zipcode');
  };

  const handleChange = (name: string, value?: string) => {
    value && resetForm(name);

    if (name === 'pwrd') {
      validatePassword(value || '');
    }

    setFormData({ [name]: value });
  };

  const validatePassword = (value: string) => {
    const str = value;
    const passwordArr = [...passwordConditions];
    if (passwordArr[0]) {
      passwordArr[0].isValid = 8 <= str.length && str.length <= 20;
    }
    if (passwordArr[1]) {
      passwordArr[1].isValid = !!str.match(/[A-Z]/g);
    }
    if (passwordArr[2]) {
      passwordArr[2].isValid = !!str.match(/[a-z]/g);
    }
    if (passwordArr[3]) {
      passwordArr[3].isValid = !!str.match(/^(?=.*[0-9$!#&@?%=_])[A-Za-z0-9$!#&@?%=_]{1,}$/g);
    }
    setPasswordConditions([...passwordArr]);
  };

  const resetForm = (fieldName: string) => {
    const newErrors: any = { ...errors };
    switch (fieldName) {
      case 'firstName':
        newErrors.firstNameError = '';
        break;
      case 'lastName':
        newErrors.lastNameError = '';
        break;
      case 'zipcode':
        newErrors.zipCodeError = '';
        newErrors.zipCodeInvalidError = '';
        newErrors.usPostalCodeInvalidError = '';
        newErrors.canadaPostalCodeInvalidError = '';
        break;
      case 'mobilePhone':
        newErrors.mobilePhoneError = '';
        newErrors.mobilePhoneRequired = '';
        newErrors.mobilePhoneDigitsRequired = '';
        newErrors.phoneAlreadyAssociatedError = '';
        newErrors.mobilePhoneInvalidError = '';
        break;
      case 'verificationCodeRequired':
        newErrors.verificationCodeRequired = '';
        break;
      case 'email':
        newErrors.emailErrorLabel = '';
        newErrors.emailInvalidError = '';
        newErrors.existingAccountInlineErrorMessage = '';
        break;
      case 'pwrd':
        newErrors.pwrdErrorLabel = '';
        break;
      case 'confirmPwrd':
        newErrors.confirmAndNewPwrdMismatchMsg = '';
        break;
      case 'pinyinEnglishSurname':
        newErrors.chinaSurNameError = '';
        newErrors.errorPinyinLastName = '';
        newErrors.inlineErrorLastNameCharLimit = '';
        break;
      case 'pinyinEnglishName':
        newErrors.chinaNameError = '';
        newErrors.errorPinyinFirstName = '';
        newErrors.inlineErrorFirstNameCharLimit = '';
        break;
      default:
    }
    setErrors(newErrors);
  };

  const validateAllFields = () => {
    const newErrors: CreateAccountErrors = {};
    const inlineErrors: CreateAccountErrors = {};
    let isValid = true;

    if (!isChinaLocale) {
      if (!formData?.firstName) {
        newErrors.firstNameError = firstNameError;
        isValid = false;
      }

      if (!formData?.lastName) {
        newErrors.lastNameError = lastNameError;
        isValid = false;
      }
      if (formData?.firstName) {
        if (!nameFieldRegex.test(formData?.firstName)) {
          newErrors.errorFirstName = firstNameBannerError;
          isValid = false;
        }
      }

      if (formData?.lastName) {
        if (!nameFieldRegex.test(formData?.lastName)) {
          newErrors.errorLastName = lastNameBannerError;
          isValid = false;
        }
      }
    } else {
      if (chineseCharInput?.firstNameInlineErrorFlag) {
        newErrors.errorFirstName = incorrectChineseFirstNameFormat;
        isValid = false;
      }

      if (chineseCharInput?.lastNameInlineErrorFlag) {
        newErrors.errorLastName = incorrectChineseLastNameFormat;
        isValid = false;
      }

      if (formData?.pinyinEnglishName) {
        if (!nameFieldRegex.test(formData?.pinyinEnglishName?.trim())) {
          newErrors.errorPinyinFirstName = incorrectPinyinEnglishFirstNameFormat;
          isValid = false;
        }
      }

      if (formData?.pinyinEnglishSurname) {
        if (!nameFieldRegex.test(formData?.pinyinEnglishSurname?.trim())) {
          newErrors.errorPinyinLastName = incorrectPinyinEnglishLastNameFormat;
          isValid = false;
        }
      }
    }

    if (isMobileAuthFeatureEnabled || isChinaLocale) {
      if (!formData?.pinyinEnglishSurname) {
        newErrors.chinaSurNameError = chinaSurNameError;
        isValid = false;
      }

      if (!formData?.pinyinEnglishName) {
        newErrors.chinaNameError = chinaNameError;
        isValid = false;
      }
    }

    if (showZipcodeField) {
      if (!formData?.zipcode?.trim()) {
        newErrors.zipCodeError = zipCodeError;
        isValid = false;
      } else if (!checkForValidZipCode(formData?.zipcode, formData?.country?.id)) {
        newErrors.zipCodeInvalidError = zipCodeInvalidError;
        isValid = false;
      }
    }

    if (isPhoneNumberAndMFARequired || isPhoneNumberRequired) {
      if (!formData?.email) {
        newErrors.emailErrorLabel = emailErrorLabel;
        isValid = false;
      }
    } else {
      if (!formData?.email && !disableField && (!mobileField || !(mobileField && !mobileFieldOptional))) {
        newErrors.emailErrorLabel = emailErrorLabel;
        isValid = false;
      }
    }
    // check if length is less than 5 or it is of incorrect format according to regex
    if (
      (!disableField || isPhoneNumberAndMFARequired) &&
      formData?.email?.trim() &&
      (formData?.email?.trim()?.length < 5 || !emailRegex.test(formData.email?.trim()))
    ) {
      newErrors.emailInvalidError = emailInvalidError;
      isValid = false;
    }

    if (!formData?.pwrd && !disableField) {
      newErrors.pwrdErrorLabel = pwrdErrorLabel;
      isValid = false;
    }

    if ((formData?.pwrd && !formData?.confirmPwrd) || formData.confirmPwrd !== formData.pwrd) {
      newErrors.confirmAndNewPwrdMismatchMsg = confirmAndNewPwrdMismatchMsg;
      isValid = false;
    }
    if (formData.confirmPwrd !== formData.pwrd) {
      newErrors.confirmAndNewPwrdMismatchMsg = confirmAndNewPwrdMismatchMsg;
      isValid = false;
    }

    if (
      mobileField &&
      !disableField &&
      (!mobileFieldOptional || isPatternThree) &&
      (!isPhoneNumberRequired || isPhoneNumberAndMFARequired)
    ) {
      if (!formData?.verificationCode) {
        updatePhoneNumberAndOtpErrorInDatalayer(false, true);
        newErrors.verificationCodeRequired = verificationCodeRequired;
        if (eaa && prefillObj?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_FOUR) {
          inlineErrors.verificationCodeInlineError = verificationCodeInlineError;
        }
        isValid = false;
      }
    }

    if (disableField && mobileField && !mobileFieldOptional) {
      //the mobile field is empty mobilePhoneError shows mobile phone required error message
      if (!formData?.mobilePhone) {
        if (isPhoneNumberRequired) {
          newErrors.mobilePhoneRequired = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
          updatePhoneNumberAndOtpErrorInDatalayer();
        } else if (eaa && prefillObj?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_THREE) {
          newErrors.mobilePhoneRequired = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
          inlineErrors.mobilePhoneEmptyError = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
        } else if (eaa && prefillObj?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_FOUR) {
          newErrors.mobilePhoneRequired = mobilePhoneError;
          inlineErrors.mobilePhoneEmptyError = mobilePhoneEmptyError;
        } else {
          newErrors.mobilePhoneRequired = mobilePhoneError;
          newErrors.mobilePhoneDigitsRequired = mobilePhoneDigitsRequired;
        }
        isValid = false;
      }
    } else if (mobileField && !mobileFieldOptional && (isPhoneNumberRequired || isPhoneNumberAndMFARequired)) {
      if (!formData?.mobilePhone) {
        newErrors.mobilePhoneRequired = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
        updatePhoneNumberAndOtpErrorInDatalayer();
        isValid = false;
      }
    } else if (eaa && isPatternThree) {
      if (mobileField && !mobileFieldOptional && !formData?.mobilePhone) {
        newErrors.mobilePhoneRequired = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
        inlineErrors.mobilePhoneEmptyError = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
        isValid = false;
      }
    }

    // add mobileField check to perform below validation when phone section is in enable mode
    if (formData?.mobilePhone && mobileField) {
      const validLength =
        formData?.country?.id?.toUpperCase() === constants.USA_COUNTRY_CODE ||
        formData?.country?.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE
          ? formData?.mobilePhone.length === 10
          : formData?.mobilePhone.length >= 7 && formData?.mobilePhone.length <= 16;

      if (
        hasPhoneNumberField &&
        ((!isChinesePhone && !validLength) ||
          (isChinesePhone && !isChinesePhoneValid) ||
          !NUMBERS_REGEX.test(formData?.mobilePhone))
      ) {
        newErrors.mobilePhoneRequired = handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError);
        newErrors.mobilePhoneInvalidError = errorBannerMsgMobNumInvalid;
        isValid = false;
        updatePhoneNumberAndOtpErrorInDatalayer();
      } else if (isPatternThree && (!validLength || !NUMBERS_REGEX.test(formData?.mobilePhone))) {
        newErrors.mobileNumberErrorMessage = models?.mobileNumberErrorMessage;
        inlineErrors.mobilePhoneInvalidError = mobilePhoneInvalidError;
        isValid = false;
      } else if (isPatternTwo && (!validLength || !NUMBERS_REGEX.test(formData?.mobilePhone))) {
        newErrors.mobileNumberErrorMessage = models?.errorBannerMsgMobNumInvalid || models?.mobileNumberErrorMessage;
        inlineErrors.mobilePhoneInvalidError = inlineErrorMsgMobNumInvalid || mobilePhoneInvalidError;
        isValid = false;
      } else if (
        (!isChinesePhone && !isValidPhoneNo) ||
        (isChinesePhone && !isChinesePhoneValid) ||
        !NUMBERS_REGEX.test(formData?.mobilePhone)
      ) {
        //the mobile field is invalid mobilePhoneRequired shows mobile phone invalid error message
        newErrors.mobilePhoneRequired = mobilePhoneRequired;
        newErrors.mobilePhoneDigitsRequired = mobilePhoneDigitsRequired;
        if (eaa && prefillObj?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_FOUR) {
          inlineErrors.mobilePhoneInvalidError = mobilePhoneInvalidError;
        }
        isValid = false;
      }
    }

    if (formData?.country?.id?.toUpperCase() === constants.KOREA_COUNTRY_CODE && Object.keys(consentErrors)?.length) {
      if (consentErrors['errorMessagePersonal']) {
        newErrors.errorMessagePersonal = consentErrors['errorMessagePersonal'];
        isValid = false;
      }
      if (consentErrors['errorMessagePromotions']) {
        newErrors.errorMessagePromotions = consentErrors['errorMessagePromotions'];
        isValid = false;
      }
    }
    setErrors({ ...newErrors, ...inlineErrors });
    // CNWEB-6635, Sorting error entries if they contain first name and last name errors
    // to match the convention of showing last name before first name (reverseNamefield == true), in locales like zh-CN, JP etc.
    if (reverseNamefield) {
      const sortedEntries = sortErrorObject(newErrors);
      setErrorsList(Object.values(sortedEntries));
    } else {
      setErrorsList(Object.values(newErrors));
    }
    !isValid && scrollIntoViewClass('create-account-description-container');
    setFormValidation(isValid);
    setJoinNowBtnClicked(false);
    if (!isValid) {
      resetPwrd();
    }
    return isValid;
  };

  useEffect(() => {
    const newErrors: any = {};
    if (errorKey) {
      errorKey?.forEach(error => {
        if (error && error in CREATE_ACCOUNT_API_ERROR) {
          const errorMsgKey = checkErrorMessage(error, isValidPartnerPortal);

          const errortargetKey =
            error === EMAIL_DUPLICATE_ERROR ? EXISTING_ACCOUNT_ERROR_MESSAGE : (error as keyof CreateAccountFormProps);

          const errorValue = !isValidPartnerPortal
            ? models?.[errorMsgKey as keyof CreateAccountFormProps]
            : partnerPortalObject?.[errorMsgKey as keyof partnerPortalListType];

          const updatedErrorValue =
            eaa &&
            ((error === DUPLICATE_PHONE_ERROR_KEY && errorMsgKey === PHONE_UNIQUENESS_ERROR_KEY) ||
              (error === EMAIL_DUPLICATE_ERROR && errorMsgKey === EMAIL_UNIQUENESS_ERROR)) &&
            typeof errorValue === 'string'
              ? // Replace login_url placeholder in emailUniquenessError with EAA-specific login URL.
                errorValue.replace('login_url', getEaaLoginUrl())
              : errorValue;

          newErrors[errortargetKey] = updatedErrorValue as any;
        } else {
          newErrors[error as keyof CreateAccountFormProps] =
            models?.[error as keyof CreateAccountFormProps] ?? pageContext?.uxlErrorMessage;
        }
      });
    }
    setErrors(newErrors);
    setErrorsList(Object.values(newErrors));
    Object.values(newErrors)?.length && scrollIntoViewClass('create-account-description-container');
    setFormValidation(false);
    setJoinNowBtnClicked(false);
    resetPwrd();
  }, [errorKey]);

  const checkProhibitedCountries = (randCountry?: string) => {
    let isInvalid = false;
    if (
      prohibitedCountriesArr?.includes(formData?.country?.id) ||
      prohibitedCountriesArr?.includes(localeCountryCode) ||
      (randCountry && prohibitedCountriesArr?.includes(randCountry))
    ) {
      const newErrors: any = {};
      newErrors.prohibitedCountryErrMsg = prohibitedCountryErrMsg;
      setErrors(newErrors);
      setErrorsList(Object.values(newErrors));
      scrollIntoViewClass('create-account-description-container');
      isInvalid = true;
      setJoinNowBtnClicked(false);
    }
    if (isInvalid) {
      resetPwrd();
    }
    return isInvalid;
  };

  // Builds EAA login URL by combining partner prefill query params with current URL params
  // while removing prefill_id from the browser query string.
  const getEaaLoginUrl = () => {
    const searchParams = new URLSearchParams(window?.location?.search || '');
    searchParams.delete('prefill_id');

    const prefillQueryParams = prefillResponse?.queryParams || '';
    const filteredWindowQueryParams = searchParams.toString();
    const separator = prefillQueryParams ? (prefillQueryParams.startsWith('?') ? '&' : '?') : '?';

    return `${constants.EAA_LOGIN_URL}${prefillQueryParams}${separator}${filteredWindowQueryParams}`;
  };

  const checkErrorMessage = (errorKey: string, isPartnerPortal: boolean) => {
    const checkErrorKey = CREATE_ACCOUNT_API_ERROR?.[errorKey as keyof typeof CREATE_ACCOUNT_API_ERROR];
    let selectedErrorKey = '';

    if (isPartnerPortal) {
      selectedErrorKey = checkErrorKey?.partnerError;
    } else {
      if (errorKey === DUPLICATE_PHONE_ERROR_KEY || errorKey === EMAIL_DUPLICATE_ERROR) {
        let url = constants.MCOM_LOGIN_URL;
        if (eaa) {
          url = getEaaLoginUrl();
        }
        selectedErrorKey = checkErrorKey.default?.replace('login_url', url) ?? '';
      } else {
        selectedErrorKey = checkErrorKey?.default ?? '';
      }
    }

    if (isPhoneNumberRequired) {
      if (errorKey === DUPLICATE_PHONE_ERROR_KEY) {
        updatePhoneNumberAndOtpErrorInDatalayer(true);
      } else if (errorKey === MOBILE_PHONE_REQUIRED_ERROR_KEY) {
        updatePhoneNumberAndOtpErrorInDatalayer();
      }
    }
    return selectedErrorKey;
  };

  // The validateAllFields() function will be executed solely after the 'joinNow' button has received a click event.
  useEffect(() => {
    if (joinNowBtnClicked) {
      if (!checkProhibitedCountries()) {
        validateAllFields();
      }
    }
  }, [joinNowBtnClicked]);

  const [chineseTranslate] = useLazyQuery(phoenixAccountTransliterate, {
    onCompleted: (data: any) => {
      return data?.transliterate?.transliteratedText?.[0];
    },
  });

  const isPhoneOrEmailValid = () => {
    const newErrors: CreateAccountErrors = {
      phoneAlreadyAssociatedError: '',
      mobilePhoneRequired: '',
      mobilePhoneDigitsRequired: '',
      mobileNumberErrorMessage: '',
      emailErrorLabel: '',
      emailInvalidError: '',
      firstNameCharLimitErrorMsg: '',
      lastNameCharLimitErrorMsg: '',
    };
    const inlineErrors: CreateAccountErrors = { mobilePhoneEmptyError: '', mobilePhoneInvalidError: '' };
    const preservedErrors = errorsList.filter(
      err => err === firstNameCharLimitErrorMsg || err === lastNameCharLimitErrorMsg
    );

    // For isPatternThree and selectedOption === EMAIL, skip mobile phone required validation on Send Code
    if (!(isPatternThree && selectedOption === constants.EMAIL)) {
      if (mobileField && !mobileFieldOptional) {
        //the mobile field is empty mobilePhoneError shows mobile phone required error message
        if (!formData?.mobilePhone) {
          if (eaa) {
            newErrors.mobilePhoneRequired = mobilePhoneError;
            inlineErrors.mobilePhoneEmptyError = mobilePhoneEmptyError;
          } else {
            newErrors.mobilePhoneRequired = mobilePhoneError;
            newErrors.mobilePhoneDigitsRequired = mobilePhoneDigitsRequired;
          }
        } else if (
          (!isChinesePhone && !isValidPhoneNo) ||
          (isChinesePhone && !isChinesePhoneValid) ||
          !NUMBERS_REGEX.test(formData?.mobilePhone)
        ) {
          newErrors.mobilePhoneRequired = isPhoneNumberAndMFARequired
            ? inlineErrorMsgMobNumMandatory
            : mobilePhoneRequired;
          if (!isPhoneNumberAndMFARequired) {
            newErrors.mobilePhoneDigitsRequired = mobilePhoneDigitsRequired;
          }
          if (eaa) {
            inlineErrors.mobilePhoneInvalidError = mobilePhoneInvalidError;
          }
          if (isPhoneNumberAndMFARequired) {
            updatePhoneNumberAndOtpErrorInDatalayer();
            newErrors.mobileNumberErrorMessage = models?.errorBannerMsgMobNumInvalid;
            inlineErrors.mobilePhoneInvalidError = inlineErrorMsgMobNumInvalid;
          }
        }
      }
    }

    //check for mobile number for pattern 3 where phone is optinal field
    if (isPatternThree && selectedOption === constants.PHONE && mobileField && formData?.mobilePhone) {
      const validLength =
        formData?.country?.id?.toUpperCase() === constants.USA_COUNTRY_CODE ||
        formData?.country?.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE
          ? formData?.mobilePhone.length === 10
          : formData?.mobilePhone.length >= 7 && formData?.mobilePhone.length <= 16;

      if (!validLength || !NUMBERS_REGEX.test(formData?.mobilePhone)) {
        newErrors.mobileNumberErrorMessage = models?.mobileNumberErrorMessage;
        inlineErrors.mobilePhoneInvalidError = mobilePhoneInvalidError;
      }
    }
    if (isPhoneNumberAndMFARequired && mobileField && formData?.mobilePhone) {
      const validLength =
        formData?.country?.id?.toUpperCase() === constants.USA_COUNTRY_CODE ||
        formData?.country?.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE
          ? formData?.mobilePhone.length === 10
          : formData?.mobilePhone.length >= 7 && formData?.mobilePhone.length <= 16;

      if (!validLength || !NUMBERS_REGEX.test(formData?.mobilePhone)) {
        newErrors.mobileNumberErrorMessage = models?.errorBannerMsgMobNumInvalid;
        inlineErrors.mobilePhoneInvalidError = inlineErrorMsgMobNumInvalid;
      }
    }

    // check for email validation for pattern 3
    if (isPatternThree && selectedOption === constants.EMAIL) {
      if (!formData?.email) {
        newErrors.emailErrorLabel = emailErrorLabel;
      }

      if (
        formData?.email?.trim() &&
        (formData?.email?.trim()?.length < 5 || !emailRegex.test(formData.email?.trim()))
      ) {
        newErrors.emailInvalidError = emailInvalidError;
      }
    }

    const errorMessageList = Object.values(newErrors).filter(value => value);
    setErrors(prevErrors => ({ ...prevErrors, ...newErrors, ...inlineErrors }));

    const updatedErrorsList =
      preservedErrors.length > 0 ? [...new Set([...preservedErrors, ...errorMessageList])] : errorMessageList;
    setErrorsList(updatedErrorsList);

    const hasErrors = errorMessageList.length === 0;
    !hasErrors && scrollIntoViewClass('create-account-description-container');
    return hasErrors;
  };

  const handleTimerActiveChange = (timerState: boolean) => {
    setIsOtpSent(timerState);
  };

  const handleOtp = async () => {
    if (isPhoneOrEmailValid()) {
      setisLoading(true);
      const phone = formData?.mobilePhone || '';
      const duration = models?.timer;
      if (!(isPatternThree && selectedOption === constants.EMAIL)) {
        const secondsLeft = calculateOtpTimeLeft(phone, otpMapRef.current, duration);
        setTimeLeft(secondsLeft);

        if (secondsLeft > 0) {
          // Timer is still active — update UI and skip API call
          setisLoading(false);
          setTimeLeft(secondsLeft);
          setShowSendCode(true);
          isPatternFour && setDisableField(false);
          return;
        }
      }
      try {
        const payload = isPatternThree
          ? {
              mfaOption: selectedOption === constants.EMAIL ? constants.OTP_EMAIL : constants.OTP_PHONE,
              ...(selectedOption === constants.EMAIL
                ? { emailAddress: formData?.email }
                : { phoneNumber: `${formData?.mobilePhoneCountryId?.value}${formData?.mobilePhone}` }),
            }
          : {
              phoneNumber: `${formData?.mobilePhoneCountryId?.value}${formData?.mobilePhone}`,
            };
        const response = await axios.post(addSubDirectoryPrefixForNonEaa(generateOneTimePwrdUrl, eaa), payload, {
          headers: {
            'Content-Type': 'application/json',
          },
          params: {
            ...(currentLocale && { locale: currentLocale }),
          },
        });
        setDisableSecondHalf(false);
        setDisableField(false);
        setTransactionDetails(response.data.transactionId);
        setIsOtpSent(true);
        setShowSendCode(true);
        setResetTimer(false);
        if (!(isPatternThree && selectedOption === constants.EMAIL)) {
          otpMapRef.current[phone] = Date.now();
          setTimeLeft(duration);
        }
      } catch (error: any) {
        const newErrors: any = {};
        if (error) {
          const errorMessages = error?.response?.data?.phoenixErrorMessages?.errorMessages || [];
          if (errorMessages.length) {
            errorMessages?.forEach((errorKey: string) => {
              errorKey === DUPLICATE_PHONE_ERROR_KEY &&
                isPhoneNumberAndMFARequired &&
                updatePhoneNumberAndOtpErrorInDatalayer(false, false, true);
              if (errorKey && errorKey in CREATE_ACCOUNT_API_ERROR) {
                const errorMsgKey = checkErrorMessage(errorKey, isValidPartnerPortal);

                const errortargetKey =
                  errorKey === EMAIL_DUPLICATE_ERROR
                    ? EXISTING_ACCOUNT_ERROR_MESSAGE
                    : (errorKey as keyof CreateAccountFormProps);

                let errorValue;
                if (!isValidPartnerPortal) {
                  errorValue = models?.[errorMsgKey as keyof CreateAccountFormProps];
                } else {
                  errorValue = partnerPortalObject?.[errorMsgKey as keyof partnerPortalListType];
                }

                // to be cleanup
                if (eaa) {
                  if (errorKey === 'phoneAlreadyAssociatedError' && errorMsgKey === 'phoneUniquenessError') {
                    const url = getEaaLoginUrl();
                    const phoneUniquenessErrorMessage =
                      models?.[errorMsgKey as keyof CreateAccountFormProps] || phoneAlreadyAssociatedError;

                    errorValue =
                      typeof phoneUniquenessErrorMessage === 'string'
                        ? phoneUniquenessErrorMessage.replace('login_url', url)
                        : phoneUniquenessErrorMessage;
                  } else if (
                    errorKey === EMAIL_DUPLICATE_ERROR &&
                    errorMsgKey === EMAIL_UNIQUENESS_ERROR &&
                    typeof errorValue === 'string'
                  ) {
                    // Keep OTP error handling aligned with the same EAA login_url replacement.
                    errorValue = errorValue.replace('login_url', getEaaLoginUrl());
                  }
                }

                newErrors[errortargetKey] = errorValue as any;
              } else {
                newErrors[errorKey as keyof CreateAccountFormProps] =
                  models?.[errorKey as keyof CreateAccountFormProps] ?? pageContext?.uxlErrorMessage;
              }
            });
          } else {
            newErrors['uxlErrorMessage'] = pageContext?.uxlErrorMessage;
          }
        }
        setErrors(newErrors);
        setErrorsList(Object.values(newErrors));
        Object.values(newErrors)?.length && scrollIntoViewClass('create-account-description-container');
      } finally {
        setisLoading(false);
      }
    }
  };

  const checkChineseValidation = (val: string, fieldType: string) => {
    if (fieldType === 'firstName') {
      if (val && !isChinese(val)) {
        setChineseCharInput({
          ...chineseCharInput,
          firstNameInlineErrorFlag: true,
        });
        setFormData({
          ...formData,
          pinyinEnglishName: '',
        });
      } else {
        setChineseCharInput({
          ...chineseCharInput,
          firstNameInlineErrorFlag: false,
        });
      }
    } else if (fieldType === 'lastName') {
      if (val && !isChinese(val)) {
        setChineseCharInput({
          ...chineseCharInput,
          lastNameInlineErrorFlag: true,
        });
        setFormData({
          ...formData,
          pinyinEnglishSurname: '',
        });
      } else {
        setChineseCharInput({
          ...chineseCharInput,
          lastNameInlineErrorFlag: false,
        });
      }
    }
    return;
  };

  const englishTranslateFromChinese = (textValue: string) => {
    return chineseTranslate({
      variables: {
        input: {
          sourceLanguage: CHINESE_SOURCE_LANG,
          sourceText: textValue,
          targetLanguage: ENGLISH_SOURCE_LANG,
        },
      },
      // skip: skipQuery,
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    });
  };

  const fetchMobileNumber = (obj: any) => {
    setShowSendCode(false);
    const countryId = updatedCountriesDialCodes?.find(country => obj.id === country.id);
    setFormData({
      mobilePhone: obj.mobileNumber,
      mobilePhoneCountryId: countryId,
    });
    setSelectedCountryCode(obj?.countryCode);

    //disable SMS phone code radio button based on phone country for pattern 3
    if (isPatternThree) {
      if (
        obj?.countryCode !== constants.USA_COUNTRY_DIAL_CODE ||
        (obj?.countryCode === constants.USA_COUNTRY_DIAL_CODE && !obj?.mobileNumber)
      ) {
        setDisablePhoneSendCode(true);
        setSelectedOption(constants.EMAIL); // revert back to email when phone radio option disabled
      } else {
        handleDefaultOtpOption(formData?.country?.id, countryId?.value, obj.mobileNumber);
        setDisablePhoneSendCode(false);
      }
    }

    if (isPhoneNumberRequired || isPhoneNumberAndMFARequired) {
      const isValidPhone =
        NUMBERS_REGEX.test(obj?.mobileNumber) && obj?.mobileNumber?.length >= 7 && obj?.mobileNumber?.length <= 16;
      if (isValidPhone) {
        setDisableField(false);
      }
      setMobileFieldOptional(false);
    }
    // add partner check to show and hide phone field based on phone country code
    else if (countryId?.value === CHINESE_MOBILE_CODE && (isPatternFour || (isChinaLocale && !eaa))) {
      setDisableField(true);
      setMobileFieldOptional(false);
    } else if (isPatternThree) {
      //always show verification and password fields in disabled when phone number changes for pattern 3
      setDisableField(true);
    } else {
      setDisableField(false);
      setMobileFieldOptional(true);
    }
  };

  const handleCheckboxChange = () => {
    setRemembermeCheckBox(!remembermeCheckBox);
  };

  const renderEmail = () => {
    return (
      <div className="row">
        <InputTextField
          label={
            isPhoneNumberAndMFARequired
              ? emailLabel
              : mobileField && !mobileFieldOptional && !isPatternThree && !isPhoneNumberRequired
              ? `${emailLabel} (${optionalLabel})`
              : emailLabel
          }
          inputValue={formData?.email}
          testId="email-label"
          showErrorMessage={
            !!errors?.emailErrorLabel || !!errors?.emailInvalidError || !!errors?.existingAccountInlineErrorMessage
          }
          messageToShow={
            errorsList?.length && errors?.existingAccountInlineErrorMessage
              ? emailDuplicateError
              : errors?.emailErrorLabel
              ? emailErrorLabel
              : emailInvalidError
          }
          setErrorHtml={true}
          messageClass="error-label"
          inputMaxLength={80}
          infoLabel={emailLabel}
          getInputProps={() => ({
            autoComplete: 'off',
            id: `${inputId}-Email`,
            disabled: disableField && !isPatternThree,
          })}
          getLabelProps={() => ({
            htmlFor: `${inputId}-Email`,
          })}
          className={clsx(
            inlineLabel ? 'm-input-text-field' : 'm-input-field',
            'col-12 pb-2 mt-5 px-2',
            errors?.emailErrorLabel || errors?.emailInvalidError || errors?.existingAccountInlineErrorMessage
              ? 'is-error'
              : formData?.email
              ? 'is-active'
              : '',
            disableField && !isPatternThree && 'is-disabled'
          )}
          getInputValue={(val: string) => {
            val && resetForm('email');
            setFormData({ ...formData, email: val });
            if (isPatternThree) {
              setShowSendCode(false);
              setDisableField(true);
            }
          }}
        />
      </div>
    );
  };

  const renderOtpTimer = () => {
    return (
      <CreateAccountOtpTimer
        showSendCode={showSendCode}
        disableField={disableField}
        enableTimer={pageContext?.enableTimer}
        otpTimer={timeLeft}
        handleOtp={handleOtp}
        handleTimerActiveChange={handleTimerActiveChange}
        isOtpSent={isOtpSent}
        timerRunningLabel={models?.timerRunningLabel}
        resendNewCodeLabel={models?.resendANewCode}
        otpValidationError={
          errors?.incorrectVerificationCode || errors?.expiredTransactionCode || hasOtpError ? true : false
        }
        incorrectCodeRunningLabel={models?.incorrectCodeRunningLabel}
        verificationCodeInlineMessage={models?.otpIncorrectErrorMessage}
        didNotGetACodeLabel={models?.didNotGetACode}
        resetTimer={resetTimer}
        incorrectVerificationCode={errors?.incorrectVerificationCode}
        expiredTransactionCode={errors?.expiredTransactionCode}
      />
    );
  };

  const renderForm = () => {
    return (
      <form
        method="post"
        data-testid="create-account-form"
        data-enrollment-variation={
          !isTargetBasedDataLayerEnabled
            ? isValidValue(experienceEnrollmentVariation)
              ? experienceEnrollmentVariation
              : undefined
            : ''
        }
      >
        <div className="create-account-form">
          <div className="row">
            <div className="col-12 col-lg-6 px-2">
              {reverseNamefield ? (
                <InputTextField
                  label={isChinaLocale ? lastNameLabelChinese : lastNameLabel}
                  inputValue={formData?.lastName}
                  showErrorMessage={
                    !!errors?.lastNameError || !!errors?.errorLastName || chineseCharInput?.lastNameInlineErrorFlag
                  }
                  messageToShow={
                    checkChinaLocale(currentLocale || '') && errors?.errorLastName
                      ? inlineErrorChineseName
                      : errors?.errorLastName
                      ? nameInlineErrorMsg
                      : errors?.lastNameError
                      ? lastNameError
                      : chineseCharInput?.lastNameInlineErrorFlag
                      ? inlineErrorChineseName
                      : ''
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-lastName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-lastName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.lastNameError || errors?.errorLastName || chineseCharInput?.lastNameInlineErrorFlag
                      ? 'is-error'
                      : formData?.lastName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('lastName');
                    setFormData({ ...formData, lastName: val?.trim() });
                    !isChinaLocale
                      ? nameRegExpValidation(val, 'lastName', 'errorLastName', setErrors, errors, nameInlineErrorMsg)
                      : checkChineseValidation(val, 'lastName');
                  }}
                  onBlur={event => {
                    isChinaLocale &&
                      !chineseCharInput?.lastNameInlineErrorFlag &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target?.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishSurname: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishSurname',
                          'errorPinyinEnglishSurNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              ) : (
                <InputTextField
                  label={isChinaLocale ? firstNameLabelChinese : firstNameLabel}
                  inputValue={formData?.firstName}
                  showErrorMessage={
                    !!errors?.firstNameError ||
                    !!errors?.errorFirstName ||
                    errorKey?.includes(nameErrorMessageCode.firstNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorFirstName
                      ? nameInlineErrorMsg
                      : errors?.firstNameError
                      ? firstNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit20.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-firstName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-firstName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.firstNameError || !!errors?.errorFirstName
                      ? 'is-error'
                      : formData?.firstName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('firstName');
                    setFormData({ ...formData, firstName: val?.trim() });
                    !isChinaLocale &&
                      nameRegExpValidation(val, 'firstName', 'errorFirstName', setErrors, errors, nameInlineErrorMsg);
                    val.trim().length <= nameLimit20 && removeErrorKey(nameErrorMessageCode.firstNameCharLimitErrorMsg);
                  }}
                  onBlur={event => {
                    isChinese(event?.target.value as string) &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishName: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishName',
                          'errorpinyinEnglishNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              )}
            </div>
            <div className="col-12 col-lg-6 px-2">
              {reverseNamefield ? (
                <InputTextField
                  label={isChinaLocale ? firstNameLabelChinese : firstNameLabel}
                  inputValue={formData?.firstName}
                  showErrorMessage={
                    !!errors?.firstNameError || !!errors?.errorFirstName || chineseCharInput?.firstNameInlineErrorFlag
                  }
                  messageToShow={
                    checkChinaLocale(currentLocale || '') && errors?.errorFirstName
                      ? inlineErrorChineseName
                      : errors?.errorFirstName
                      ? nameInlineErrorMsg
                      : errors?.firstNameError
                      ? firstNameError
                      : chineseCharInput?.firstNameInlineErrorFlag
                      ? inlineErrorChineseName
                      : ''
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={isChinaLocale ? 25 : 20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-firstName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-firstName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    chineseCharInput?.firstNameInlineErrorFlag || errors?.firstNameError || errors?.errorFirstName
                      ? 'is-error'
                      : formData?.firstName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('firstName');
                    setFormData({ ...formData, firstName: val?.trim() });
                    !isChinaLocale
                      ? nameRegExpValidation(val, 'firstName', 'errorFirstName', setErrors, errors, nameInlineErrorMsg)
                      : checkChineseValidation(val, 'firstName');
                  }}
                  onBlur={event => {
                    isChinese(event?.target.value as string) &&
                      event?.target.value !== '' &&
                      !chineseCharInput?.firstNameInlineErrorFlag &&
                      englishTranslateFromChinese(event?.target.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishName: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishName',
                          'errorpinyinEnglishNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              ) : (
                <InputTextField
                  label={isChinaLocale ? lastNameLabelChinese : lastNameLabel}
                  inputValue={formData?.lastName}
                  showErrorMessage={
                    !!errors?.lastNameError ||
                    !!errors?.errorLastName ||
                    errorKey?.includes(nameErrorMessageCode.lastNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorLastName
                      ? nameInlineErrorMsg
                      : errors?.lastNameError
                      ? lastNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit25.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    // onKeyUp: handleKeyPress,
                    id: `${inputId}-lastName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-lastName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.lastNameError || errors?.errorLastName ? 'is-error' : formData?.lastName ? 'is-active' : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('lastName');
                    setFormData({ ...formData, lastName: val?.trim() });
                    !isChinaLocale &&
                      nameRegExpValidation(val, 'lastName', 'errorLastName', setErrors, errors, nameInlineErrorMsg);
                    val.trim().length <= nameLimit25 && removeErrorKey(nameErrorMessageCode.lastNameCharLimitErrorMsg);
                  }}
                  onBlur={event => {
                    isChinaLocale &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target?.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishSurname: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishSurname',
                          'errorPinyinEnglishSurNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              )}
            </div>
          </div>
          {(isMobileAuthFeatureEnabled || isChinaLocale) && (
            <div className="row">
              <div className="col-12 col-lg-6 px-2">
                <InputTextField
                  label={pinyinEnglishSurname}
                  inputValue={formData?.pinyinEnglishSurname}
                  showErrorMessage={
                    !!errors?.chinaSurNameError ||
                    !!errors?.errorPinyinEnglishSurNameInline ||
                    !!errors?.errorPinyinLastName ||
                    !!errorKey?.includes(nameErrorMessageCode.lastNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorPinyinEnglishSurNameInline || errors?.errorPinyinLastName
                      ? nameInlineErrorMsg
                      : errors?.chinaSurNameError
                      ? chinaSurNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit25.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-pinyinEnglishSurname`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-pinyinEnglishSurname`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field pb-2',
                    'mt-5',
                    errors?.chinaSurNameError || errors?.errorPinyinLastName
                      ? 'is-error'
                      : formData?.pinyinEnglishSurname
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('pinyinEnglishSurname');
                    setFormData({ ...formData, pinyinEnglishSurname: val?.trim() });
                    nameRegExpValidation(
                      val,
                      'pinyinEnglishSurname',
                      'errorPinyinEnglishSurNameInline',
                      setErrors,
                      errors,
                      nameInlineErrorMsg
                    );
                    val.trim().length <= nameLimit25 && removeErrorKey(nameErrorMessageCode.lastNameCharLimitErrorMsg);
                  }}
                  showBadge={!inlineLabel && !!inlineMessageForName}
                  badgeValue={inlineLabel ? '' : inlineMessageForName}
                />
                {inlineLabel && <div className="t-font-alt-xs col-12 mt-1 pl-0 pr-0">{inlineMessageForName}</div>}
              </div>
              <div className="col-12 col-lg-6 px-2">
                <InputTextField
                  label={pinyinEnglishName}
                  inputValue={formData?.pinyinEnglishName}
                  showErrorMessage={
                    !!errors?.chinaNameError ||
                    !!errors?.errorpinyinEnglishNameInline ||
                    !!errors?.errorPinyinFirstName ||
                    !!errorKey?.includes(nameErrorMessageCode.firstNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorpinyinEnglishNameInline || errors?.errorPinyinFirstName
                      ? nameInlineErrorMsg
                      : errors?.chinaNameError
                      ? chinaNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit20.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-pinyinEnglishName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-pinyinEnglishName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field pb-2',
                    'mt-5',
                    errors?.chinaNameError || errors?.errorPinyinFirstName
                      ? 'is-error'
                      : formData?.pinyinEnglishName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('pinyinEnglishName');
                    setFormData({ ...formData, pinyinEnglishName: val?.trim() });
                    nameRegExpValidation(
                      val,
                      'pinyinEnglishName',
                      'errorpinyinEnglishNameInline',
                      setErrors,
                      errors,
                      nameInlineErrorMsg
                    );
                    val.trim().length <= nameLimit20 && removeErrorKey(nameErrorMessageCode.firstNameCharLimitErrorMsg);
                  }}
                  showBadge={!inlineLabel && !!inlineMessageForName}
                  badgeValue={inlineLabel ? '' : inlineMessageForName}
                />
                {inlineLabel && <div className="t-font-alt-xs col-12 mt-1 pl-0 pr-0">{inlineMessageForName}</div>}
              </div>
            </div>
          )}
          <div className="row">
            <div
              className={clsx(
                `col-12 ${showZipcodeField ? 'col-lg-6' : 'col-lg-12'} pb-2 mt-5 px-2`,
                !inlineLabel && 'm-input-field'
              )}
            >
              {!inlineLabel && (
                <label
                  className="country-label"
                  id="dropdown-label-fp-country-code"
                  htmlFor="dropdown-label-fp-country-code"
                  data-testid="dropdown-label-fp-country"
                >
                  {countryRegionLabel}
                </label>
              )}
              <Dropdown
                defaultOption={formData?.country?.value || defaultCountry?.value || ''}
                dropdownOptions={isValidClientId ? updatedSweepstakeCountriesDialCodes ?? [] : countries || []}
                onChange={handleCountryNameChange}
                customClassName={`activate_account--countrydropdown is-active ${
                  !inlineLabel && 'activate_account--countrydropdown__standard'
                }`}
                dropdownId="fp-country-code"
                hasLabel={inlineLabel ? countryRegionLabel : ''}
              />
            </div>
            {!!showZipcodeField && (
              <InputTextField
                label={zipPostalCodeLabel}
                inputValue={formData?.zipcode}
                showErrorMessage={
                  !!errors?.zipCodeError ||
                  !!errors?.zipCodeInvalidError ||
                  !!errors?.canadaPostalCodeInvalidError ||
                  !!errors?.usPostalCodeInvalidError
                }
                messageToShow={errors?.zipCodeError ? zipCodeError : zipInlineError}
                setErrorHtml={true}
                messageClass="error-label"
                inputMaxLength={10}
                getInputProps={() => ({
                  autoComplete: 'off',
                  // onKeyUp: handleKeyPress,
                  id: `${inputId}-Zipcode`,
                })}
                getLabelProps={() => ({
                  htmlFor: `${inputId}-Zipcode`,
                })}
                className={clsx(
                  inlineLabel ? 'm-input-text-field' : 'm-input-field',
                  'col-12 col-lg-6 pb-2 mt-5 px-2',
                  errors?.zipCodeError || errors?.zipCodeInvalidError
                    ? 'is-error'
                    : formData?.zipcode
                    ? 'is-active'
                    : ''
                )}
                getInputValue={(val: string) => {
                  val && resetForm('zipcode');
                  setFormData({ ...formData, zipcode: val });
                }}
              />
            )}
          </div>
          {(disableField || mobileField || hasPhoneNumberField) && (
            <div className="row">
              <div className="col-12 px-1">
                <div className={`${!inlineLabel && 'm-input-field'} py-2 mt-5 phone-field`}>
                  <PhoneNumberField
                    inputTextLabel={
                      mobileFieldOptional
                        ? `${mobilePhoneLabelText} (${optionalLabel})`
                        : isPhoneNumberRequired || isPhoneNumberAndMFARequired
                        ? mobilePhoneLabelText
                        : mobilePhoneLabel
                    }
                    label={countryCodeLabel}
                    defaultCCOption={
                      formData?.mobilePhoneCountryId?.id ??
                      (isValidClientId && !isChinaLocale
                        ? (updatedCountriesDialCodes ?? [])?.find(c => c?.id === defaultCountry?.id)?.id ??
                          constants.USA_COUNTRY_CODE
                        : constants.CHINA_COUNTRY_CODE)
                    }
                    dropdownCCOptions={updatedCountriesDialCodes ?? []}
                    mobileInputVal={formData?.mobilePhone?.replace(selectedCountryCode || '', '') || ''}
                    isInputError={
                      !!errors?.mobilePhoneError ||
                      !!errors?.mobilePhoneRequired ||
                      !!errors.phoneAlreadyAssociatedError ||
                      !!errors.mobilePhoneInvalidError
                    }
                    inputErrorMsg={
                      (errors.phoneAlreadyAssociatedError
                        ? handleFallback(existingMobPhoneNumWarning, existingPhoneNumberWarning)
                        : errors.mobilePhoneInvalidError
                        ? inlineErrorMsgMobNumInvalid
                        : isPhoneNumberRequired || isPhoneNumberAndMFARequired
                        ? handleFallback(inlineErrorMsgMobNumMandatory, mobilePhoneError)
                        : mobilePhoneError) ?? ''
                    }
                    disabled={false}
                    fetchMobileNumber={(obj: any) => fetchMobileNumber(obj)}
                    isNonChinaMobileEnabled={!isNonChinaMobileEnabled}
                    isRegisteredNumber={false}
                    isMobileEditable={true}
                    showIdandValueinOptions={true}
                    badgeProp={
                      (((isMobileAuthFeatureEnabled || isChinaLocale) &&
                        formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                        formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE) ||
                        isPhoneNumberAndMFARequired) &&
                      sendVerificationDescription
                    }
                    inlineLabel={inlineLabel}
                    resetForm={(fieldName: string) => resetForm(fieldName)}
                  />
                  {(((isMobileAuthFeatureEnabled || isChinaLocale) &&
                    formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                    formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE) ||
                    isPhoneNumberAndMFARequired) && (
                    <Button
                      type={Types.ButtonTypeVariation.Button}
                      buttonCopy={''}
                      className={clsx(
                        'm-button-m m-button-primary submit-btn',
                        isPhoneNumberAndMFARequired && 'mt-5 mt-sm-1',
                        showSendCode || isLoading || !formData?.mobilePhone ? 'disabled' : ''
                      )}
                      isLink={false}
                      callback={handleOtp}
                      isDisabled={showSendCode || isLoading}
                    >
                      <div
                        className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')}
                        data-testid="createAccount-spinner"
                      ></div>
                      <div className={clsx(isLoading ? 'd-none' : '')}>{sendVerificationLabel}</div>
                    </Button>
                  )}
                </div>
                {(((isMobileAuthFeatureEnabled || isChinaLocale) &&
                  formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                  formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE) ||
                  isPhoneNumberAndMFARequired) && (
                  <StyledInputTextFieldNumber
                    className={clsx(!inlineLabel && 'm-input-field', 'py-2 mt-5', disableField ? 'is-disabled' : '')}
                  >
                    <InputTextField
                      iconClass="icon icon-clear"
                      // showIcon={otpError.otp !== undefined}
                      type="number"
                      label={verificationCodeLabel}
                      inputValue={formData.verificationCode}
                      showErrorMessage={!!errors?.verificationCodeRequired}
                      messageToShow={verificationCodeRequired}
                      setErrorHtml={true}
                      messageClass="error-label"
                      className={clsx(
                        inlineLabel ? 'm-input-text-field' : 'm-input-field',
                        (disableField || disableSecondHalf) && 'is-disabled'
                      )}
                      inputMaxLength={10}
                      getInputValue={(val: string) => {
                        val && resetForm('verificationCodeRequired');
                        setFormData({ ...formData, verificationCode: val });
                      }}
                      getLabelProps={() => ({
                        htmlFor: `${inputId}-verificationCode`,
                      })}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        // onKeyUp: handleKeyPress,
                        id: `${inputId}-verificationCode`,
                        disabled: disableField || disableSecondHalf,
                      })}
                      disabled={disableField || disableSecondHalf}
                    />
                    {renderOtpTimer()}
                  </StyledInputTextFieldNumber>
                )}
              </div>
            </div>
          )}
          {renderEmail()}
          <div className="row">
            <div className="col-12 col-lg-6 mt-5 px-2">
              <PasswordInput
                value={formData.pwrd}
                id="password"
                name="pwrd"
                disable={disableField || disableSecondHalf}
                hasError={!!errors?.pwrdErrorLabel || !!errors?.confirmAndNewPwrdMismatchMsg}
                label={pwrdLabel}
                passwordType={passwordType}
                error={errors?.pwrdErrorLabel || errors?.confirmAndNewPwrdMismatchMsg}
                customClass={clsx(
                  'mb-2',
                  (disableField || disableSecondHalf) && 'is-disabled',
                  'create-account-password'
                )}
                iconClass={`password-icon ${passwordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}`}
                labelClass="t-body-s"
                showValidations={true}
                passwordConditions={passwordConditions}
                handleChange={handleChange}
                onIconClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
                isValid={passwordValid}
                showCheckIcon={true}
                ariaLabel="createaccount-password"
                descriptionClass="password-checks__description"
                errorLabelClass={`password-checks__errorlabel ${inlineLabel ? 'error-label' : ''}`}
                innerLabel={inlineLabel}
                checkHeaderLabel={pwrdHeader}
              />
            </div>
            <div className="col-12 col-lg-6 pb-2 mt-5 px-2">
              <PasswordInput
                value={formData.confirmPwrd}
                id="confirmPassword"
                name="confirmPwrd"
                disable={isValidChangePassword}
                hasError={false}
                label={confirmPwrdLabel}
                passwordType={confirmPasswordType}
                customClass="mb-4"
                iconClass={confirmPasswordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}
                labelClass="t-body-s"
                handleChange={handleChange}
                onIconClick={() => setConfirmPasswordType(confirmPasswordType === 'password' ? 'text' : 'password')}
                isValid={formData.confirmPwrd?.length && formData.confirmPwrd === formData.pwrd ? true : false}
                showCheckIcon={true}
                ariaLabel="createaccount-confirm-password"
                descriptionClass="password-checks__description"
                errorLabelClass={`password-checks__errorlabel ${inlineLabel ? 'error-label' : ''}`}
                innerLabel={inlineLabel}
              />
            </div>
          </div>
        </div>
      </form>
    );
  };

  const renderEAAForm = () => {
    return (
      <form method="post" data-testid="create-account-form">
        <div className="create-account-form">
          <div className="row">
            <div className="col-12 col-lg-6 px-2">
              {reverseNamefield ? (
                <InputTextField
                  label={isChinaLocale ? lastNameLabelChinese : lastNameLabel}
                  inputValue={formData?.lastName}
                  showErrorMessage={
                    !!errors?.lastNameError || !!errors?.errorLastName || chineseCharInput?.lastNameInlineErrorFlag
                  }
                  messageToShow={
                    checkChinaLocale(currentLocale || '') && errors?.errorLastName
                      ? inlineErrorChineseName
                      : errors?.errorLastName
                      ? nameInlineErrorMsg
                      : errors?.lastNameError
                      ? lastNameError
                      : chineseCharInput?.lastNameInlineErrorFlag
                      ? inlineErrorChineseName
                      : ''
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    // onKeyUp: handleKeyPress,
                    id: `${inputId}-lastName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-lastName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.lastNameError || errors?.errorLastName || chineseCharInput?.lastNameInlineErrorFlag
                      ? 'is-error'
                      : formData?.lastName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('lastName');
                    setFormData({ ...formData, lastName: val?.trim() });
                    !isChinaLocale
                      ? nameRegExpValidation(val, 'lastName', 'errorLastName', setErrors, errors, nameInlineErrorMsg)
                      : checkChineseValidation(val, 'lastName');
                  }}
                  onBlur={event => {
                    isChinaLocale &&
                      !chineseCharInput?.lastNameInlineErrorFlag &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target?.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishSurname: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishSurname',
                          'errorPinyinEnglishSurNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              ) : (
                <InputTextField
                  label={isChinaLocale ? firstNameLabelChinese : firstNameLabel}
                  inputValue={formData?.firstName}
                  showErrorMessage={
                    !!errors?.firstNameError ||
                    !!errors?.errorFirstName ||
                    errorKey?.includes(nameErrorMessageCode.firstNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorFirstName
                      ? nameInlineErrorMsg
                      : errors?.firstNameError
                      ? firstNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit20.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-firstName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-firstName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.firstNameError || errors?.errorFirstName
                      ? 'is-error'
                      : formData?.firstName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('firstName');
                    setFormData({ ...formData, firstName: val?.trim() });
                    !isChinaLocale &&
                      nameRegExpValidation(val, 'firstName', 'errorFirstName', setErrors, errors, nameInlineErrorMsg);
                    val.trim().length <= nameLimit20 && removeErrorKey(nameErrorMessageCode.firstNameCharLimitErrorMsg);
                  }}
                  onBlur={event => {
                    isChinese(event?.target.value as string) &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishName: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishName',
                          'errorpinyinEnglishNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              )}
            </div>
            <div className="col-12 col-lg-6 px-2">
              {reverseNamefield ? (
                <InputTextField
                  label={isChinaLocale ? firstNameLabelChinese : firstNameLabel}
                  inputValue={formData?.firstName}
                  showErrorMessage={
                    !!errors?.firstNameError || !!errors?.errorFirstName || chineseCharInput?.firstNameInlineErrorFlag
                  }
                  messageToShow={
                    checkChinaLocale(currentLocale || '') && errors?.errorFirstName
                      ? inlineErrorChineseName
                      : errors?.errorFirstName
                      ? nameInlineErrorMsg
                      : errors?.firstNameError
                      ? firstNameError
                      : chineseCharInput?.firstNameInlineErrorFlag
                      ? inlineErrorChineseName
                      : ''
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={isChinaLocale ? 25 : 20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-firstName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-firstName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    chineseCharInput?.firstNameInlineErrorFlag || errors?.firstNameError || errors?.errorFirstName
                      ? 'is-error'
                      : formData?.firstName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('firstName');
                    setFormData({ ...formData, firstName: val?.trim() });
                    !isChinaLocale
                      ? nameRegExpValidation(val, 'firstName', 'errorFirstName', setErrors, errors, nameInlineErrorMsg)
                      : checkChineseValidation(val, 'firstName');
                  }}
                  onBlur={event => {
                    isChinese(event?.target.value as string) &&
                      !chineseCharInput?.firstNameInlineErrorFlag &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishName: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishName',
                          'errorpinyinEnglishNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              ) : (
                <InputTextField
                  label={isChinaLocale ? lastNameLabelChinese : lastNameLabel}
                  inputValue={formData?.lastName}
                  showErrorMessage={
                    !!errors?.lastNameError ||
                    !!errors?.errorLastName ||
                    errorKey?.includes(nameErrorMessageCode.lastNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorLastName
                      ? nameInlineErrorMsg
                      : errors?.lastNameError
                      ? lastNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit25.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    // onKeyUp: handleKeyPress,
                    id: `${inputId}-lastName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-lastName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.lastNameError || errors?.errorLastName ? 'is-error' : formData?.lastName ? 'is-active' : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('lastName');
                    setFormData({ ...formData, lastName: val?.trim() });
                    !isChinaLocale &&
                      nameRegExpValidation(val, 'lastName', 'errorLastName', setErrors, errors, nameInlineErrorMsg);
                    val.trim().length <= nameLimit25 && removeErrorKey(nameErrorMessageCode.lastNameCharLimitErrorMsg);
                  }}
                  onBlur={event => {
                    isChinaLocale &&
                      event?.target.value !== '' &&
                      englishTranslateFromChinese(event?.target?.value as string).then(data => {
                        setFormData({
                          ...formData,
                          pinyinEnglishSurname: data?.data?.transliterate?.transliteratedText?.[0],
                        });
                        nameRegExpValidation(
                          data?.data?.transliterate?.transliteratedText?.[0],
                          'pinyinEnglishSurname',
                          'errorPinyinEnglishSurNameInline',
                          setErrors,
                          errors,
                          nameInlineErrorMsg
                        );
                      });
                  }}
                />
              )}
            </div>
          </div>
          {(isMobileAuthFeatureEnabled || isChinaLocale) && (
            <div className="row">
              <div className="col-12 col-lg-6 px-2">
                <InputTextField
                  label={pinyinEnglishSurname}
                  inputValue={formData?.pinyinEnglishSurname}
                  showErrorMessage={
                    !!errors?.chinaSurNameError ||
                    !!errors?.errorPinyinEnglishSurNameInline ||
                    !!errors?.errorPinyinLastName ||
                    !!errorKey?.includes(nameErrorMessageCode.lastNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorPinyinEnglishSurNameInline || errors?.errorPinyinLastName
                      ? nameInlineErrorMsg
                      : errors?.chinaSurNameError
                      ? chinaSurNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit25.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={25}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-pinyinEnglishSurname`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-pinyinEnglishSurname`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'mt-5',
                    errors?.chinaSurNameError || errors?.errorPinyinLastName
                      ? 'is-error'
                      : formData?.pinyinEnglishSurname
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('pinyinEnglishSurname');
                    setFormData({ ...formData, pinyinEnglishSurname: val?.trim() });
                    nameRegExpValidation(
                      val,
                      'pinyinEnglishSurname',
                      'errorPinyinEnglishSurNameInline',
                      setErrors,
                      errors,
                      nameInlineErrorMsg
                    );
                    val.trim().length <= nameLimit25 && removeErrorKey(nameErrorMessageCode.lastNameCharLimitErrorMsg);
                  }}
                  showBadge={!inlineLabel && !!inlineMessageForName}
                  badgeValue={inlineLabel ? '' : inlineMessageForName}
                />
                {inlineLabel && <div className="t-font-alt-xs col-12 mt-1 pl-0 pr-0">{inlineMessageForName}</div>}
              </div>
              <div className="col-12 col-lg-6 px-2">
                <InputTextField
                  label={pinyinEnglishName}
                  inputValue={formData?.pinyinEnglishName}
                  showErrorMessage={
                    !!errors?.errorpinyinEnglishNameInline ||
                    !!errors?.errorPinyinFirstName ||
                    !!errors?.chinaNameError ||
                    !!errorKey?.includes(nameErrorMessageCode.firstNameCharLimitErrorMsg)
                  }
                  messageToShow={
                    errors?.errorpinyinEnglishNameInline || errors?.errorPinyinFirstName
                      ? nameInlineErrorMsg
                      : errors?.chinaNameError
                      ? chinaNameError
                      : replaceText(inlineErrorNameCharLimit || '', [nameLimit20.toString()])
                  }
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={20}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    id: `${inputId}-pinyinEnglishName`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-pinyinEnglishName`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'mt-5',
                    errors?.chinaNameError || errors?.errorPinyinFirstName
                      ? 'is-error'
                      : formData?.pinyinEnglishName
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('pinyinEnglishName');
                    setFormData({ ...formData, pinyinEnglishName: val?.trim() });
                    nameRegExpValidation(
                      val,
                      'pinyinEnglishName',
                      'errorpinyinEnglishNameInline',
                      setErrors,
                      errors,
                      nameInlineErrorMsg
                    );
                    val.trim().length <= nameLimit20 && removeErrorKey(nameErrorMessageCode.firstNameCharLimitErrorMsg);
                  }}
                  showBadge={!inlineLabel && !!inlineMessageForName}
                  badgeValue={inlineLabel ? '' : inlineMessageForName}
                />
                {inlineLabel && <div className="t-font-alt-xs col-12 mt-1 pl-0 pr-0">{inlineMessageForName}</div>}
              </div>
            </div>
          )}
          <div className="row">
            <div
              className={clsx(
                `col-12 px-2 ${showZipcodeField ? 'col-lg-6' : 'col-lg-12'} ${inlineLabel ? 'pb-2 mt-5' : ''}`
              )}
            >
              {inlineLabel ? (
                <Dropdown
                  defaultOption={formData?.country?.value || defaultCountry?.value || ''}
                  dropdownOptions={isValidClientId ? updatedSweepstakeCountriesDialCodes ?? [] : countries || []}
                  onChange={handleCountryNameChange}
                  customClassName="activate_account--countrydropdown is-active"
                  dropdownId="fp-country-code"
                  hasLabel={countryRegionLabel}
                />
              ) : (
                <div className="m-input-field pb-2 mt-5">
                  <label
                    className="country-label"
                    id="dropdown-label-fp-country-code"
                    htmlFor="dropdown-label-fp-country-code"
                    data-testid="dropdown-label-fp-country"
                  >
                    {countryRegionLabel}
                  </label>
                  <Dropdown
                    defaultOption={formData?.country?.value || defaultCountry?.value || ''}
                    dropdownOptions={isValidClientId ? updatedSweepstakeCountriesDialCodes ?? [] : countries || []}
                    onChange={handleCountryNameChange}
                    customClassName="activate_account--countrydropdown__standard is-active"
                    dropdownId="fp-country-code"
                  />
                </div>
              )}
            </div>
            {!!showZipcodeField && (
              <div className="col-12 col-lg-6 px-2">
                <InputTextField
                  label={zipPostalCodeLabel}
                  inputValue={formData?.zipcode}
                  showErrorMessage={
                    !!errors?.zipCodeError ||
                    !!errors?.zipCodeInvalidError ||
                    !!errors?.canadaPostalCodeInvalidError ||
                    !!errors?.usPostalCodeInvalidError
                  }
                  messageToShow={errors?.zipCodeError ? zipCodeError : zipInlineError}
                  setErrorHtml={true}
                  messageClass="error-label"
                  inputMaxLength={10}
                  getInputProps={() => ({
                    autoComplete: 'off',
                    // onKeyUp: handleKeyPress,
                    id: `${inputId}-Zipcode`,
                  })}
                  getLabelProps={() => ({
                    htmlFor: `${inputId}-Zipcode`,
                  })}
                  className={clsx(
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                    'pb-2 mt-5',
                    errors?.zipCodeError || errors?.zipCodeInvalidError
                      ? 'is-error'
                      : formData?.zipcode
                      ? 'is-active'
                      : ''
                  )}
                  getInputValue={(val: string) => {
                    val && resetForm('zipcode');
                    setFormData({ ...formData, zipcode: val });
                  }}
                />
              </div>
            )}
          </div>
          {(isPatternTwo || isPatternThree) && renderEmail()}
          {(disableField || mobileField) && (
            <div className="row">
              <div className="col-12 px-1">
                <div className={`${!inlineLabel ? 'm-input-field' : ''} py-5 phone-field`}>
                  <PhoneNumberField
                    inputTextLabel={
                      mobileFieldOptional ? `${mobilePhoneLabelText} (${optionalLabel})` : mobilePhoneLabelText
                    }
                    label={countryCodeLabel}
                    defaultCCOption={
                      formData?.mobilePhoneCountryId?.id
                        ? formData?.mobilePhoneCountryId?.id
                        : (updatedCountriesDialCodes ?? [])?.find(country => country?.id === defaultCountry?.id)?.id ??
                          constants?.USA_COUNTRY_CODE
                    }
                    dropdownCCOptions={updatedCountriesDialCodes ?? []}
                    mobileInputVal={formData?.mobilePhone?.replace(selectedCountryCode || '', '') || ''}
                    isInputError={
                      !!errors?.mobilePhoneError ||
                      !!errors?.mobilePhoneRequired ||
                      !!errors?.phoneAlreadyAssociatedError ||
                      !!errors?.mobileNumberErrorMessage
                    }
                    inputErrorMsg={
                      (errors.phoneAlreadyAssociatedError
                        ? handleFallback(existingMobPhoneNumWarning, existingPhoneNumberWarning)
                        : errors?.mobilePhoneEmptyError || errors?.mobilePhoneInvalidError || mobilePhoneError) ?? ''
                    }
                    disabled={false}
                    fetchMobileNumber={(obj: any) => fetchMobileNumber(obj)}
                    isNonChinaMobileEnabled={disablePhoneCountry ? false : !isNonChinaMobileEnabled}
                    isRegisteredNumber={false}
                    isMobileEditable={true}
                    showIdandValueinOptions={true}
                    badgeProp={
                      mobileField &&
                      !mobileFieldOptional &&
                      formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                      formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE
                        ? sendVerificationDescription
                        : ''
                    }
                    inlineLabel={inlineLabel}
                    resetForm={(fieldName: string) => resetForm(fieldName)}
                  />
                  {mobileField &&
                    !mobileFieldOptional &&
                    formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                    formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE && (
                      <Button
                        type={Types.ButtonTypeVariation.Button}
                        buttonCopy={''}
                        className={clsx(
                          'm-button-m m-button-primary submit-btn',
                          showSendCode || isLoading || !formData?.mobilePhone ? 'disabled' : ''
                        )}
                        isLink={false}
                        callback={handleOtp}
                        isDisabled={showSendCode || isLoading}
                        custom_click_track_value={`${getCustomClickTrackingPageName()}|${
                          TRACKING_CONSTANTS.SEND_CODE_SUBMIT
                        }|${TRACKING_CONSTANTS.INTERNAL}|`}
                      >
                        {isLoading ? <div className="m-spinner-s"></div> : <div>{sendVerificationLabel}</div>}
                      </Button>
                    )}
                </div>
                {isPatternThree && (
                  <CreateAccountConfirmIdentity
                    confirmIdentityHeader={models?.confirmIdentityHeader}
                    confirmIdentitySubHeader={models?.confirmIdentitySubHeader}
                    sendCodePhoneLabel={models?.sendCodePhoneLabel}
                    selectedOption={selectedOption}
                    customClickTrackValue={`${getCustomClickTrackingPageName()}|${
                      TRACKING_CONSTANTS.SEND_CODE_SUBMIT
                    }|${TRACKING_CONSTANTS.INTERNAL}|`}
                    handlePhoneCodeChange={() => {
                      setSelectedOption(constants.PHONE);
                      setShowSendCode(false);
                      setDisableField(true);
                    }}
                    disablePhoneSendCode={disablePhoneSendCode}
                    sendCodeEmailLabel={models?.sendCodeEmailLabel}
                    handleEmailCodeChange={() => {
                      setSelectedOption(constants.EMAIL);
                      setShowSendCode(false);
                      setDisableField(true);
                    }}
                    showSendCode={showSendCode}
                    handleOtp={handleOtp}
                    isLoading={isLoading}
                    sendCodeButtonLabel={models?.sendCodeButtonLabel}
                    disableField={disableField}
                    enableTimer={pageContext?.enableTimer}
                    handleTimerActiveChange={handleTimerActiveChange}
                    isOtpSent={isOtpSent}
                    timerRunningLabel={models?.timerRunningLabel}
                    timer={timeLeft}
                    resendNewCodeLabel={models?.resendANewCode}
                    otpValidationError={
                      errors?.incorrectVerificationCode || errors?.expiredTransactionCode || hasOtpError ? true : false
                    }
                    incorrectCodeRunningLabel={models?.incorrectCodeRunningLabel}
                    verificationCodeInlineMessage={models?.otpIncorrectErrorMessage}
                    didNotGetACodeLabel={models?.didNotGetACode}
                    resetTimer={resetTimer}
                    incorrectVerificationCode={errors?.incorrectVerificationCode}
                    expiredTransactionCode={errors?.expiredTransactionCode}
                  />
                )}
                {((mobileField &&
                  !mobileFieldOptional &&
                  formData?.country?.id === constants?.CHINA_COUNTRY_CODE &&
                  formData?.mobilePhoneCountryId?.value === CHINESE_MOBILE_CODE) ||
                  isPatternThree) && (
                  <StyledInputTextFieldNumber
                    className={clsx(
                      inlineLabel ? 'm-input-text-field' : 'm-input-field',
                      'py-2 mt-5',
                      disableField ? 'disableField' : ''
                    )}
                  >
                    <InputTextField
                      iconClass="icon icon-clear"
                      // showIcon={otpError.otp !== undefined}
                      type="number"
                      label={verificationCodeLabel}
                      inputValue={formData.verificationCode}
                      showErrorMessage={!!errors?.verificationCodeRequired}
                      messageToShow={
                        errors?.verificationCodeInlineError
                          ? errors?.verificationCodeInlineError
                          : verificationCodeRequired
                      }
                      setErrorHtml={true}
                      messageClass={clsx('error-label', disableField && 'is-disabled')}
                      className={clsx(
                        inlineLabel ? 'm-input-text-field' : 'm-input-field',
                        disableField && 'is-disabled'
                      )}
                      inputMaxLength={10}
                      getInputValue={(val: string) => {
                        val && resetForm('verificationCodeRequired');
                        setFormData({ ...formData, verificationCode: val });
                      }}
                      getLabelProps={() => ({
                        htmlFor: `${inputId}-verificationCode`,
                      })}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        // onKeyUp: handleKeyPress,
                        id: `${inputId}-verificationCode`,
                        disabled: disableField,
                      })}
                      disabled={disableField}
                    />
                    {(!isPatternThree || isPhoneNumberAndMFARequired) && renderOtpTimer()}
                  </StyledInputTextFieldNumber>
                )}
              </div>
            </div>
          )}
          {!(isPatternTwo || isPatternThree) && renderEmail()}
          <div className={clsx('row', disableField && 'disableField')}>
            <div className="col-12 col-lg-6 mt-5 create-password-eaa px-2">
              <PasswordInput
                value={formData.pwrd}
                id="password"
                name="pwrd"
                disable={disableField || disableSecondHalf}
                hasError={!!errors?.pwrdErrorLabel || !!errors?.confirmAndNewPwrdMismatchMsg}
                label={createPwrdLabel || ''} // to add label from AEM for createPassword and remove condition
                passwordType={passwordType}
                error={errors?.pwrdErrorLabel || errors?.confirmAndNewPwrdMismatchMsg}
                customClass={clsx('mb-2 ', (disableField || disableSecondHalf) && 'is-disabled')}
                iconClass={`password-icon ${passwordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}`}
                labelClass="t-body-s"
                showValidations={true}
                passwordConditions={passwordConditions}
                handleChange={handleChange}
                onIconClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
                isValid={passwordValid}
                showCheckIcon={true}
                ariaLabel="createaccount-password"
                descriptionClass="password-checks__description"
                errorLabelClass={`password-checks__errorlabel ${inlineLabel ? 'error-label' : ''}`}
                innerLabel={inlineLabel}
                checkHeaderLabel={pwrdHeader}
              />
            </div>
            <div className="col-12 col-lg-6 mt-5 px-2">
              <PasswordInput
                value={formData.confirmPwrd}
                id="confirmPassword"
                name="confirmPwrd"
                disable={isValidChangePassword}
                hasError={false}
                label={confirmPwrdLabel}
                passwordType={confirmPasswordType}
                customClass="mb-lg-4"
                iconClass={confirmPasswordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}
                labelClass="t-body-s"
                handleChange={handleChange}
                onIconClick={() => setConfirmPasswordType(confirmPasswordType === 'password' ? 'text' : 'password')}
                isValid={formData.confirmPwrd?.length && formData.confirmPwrd === formData.pwrd ? true : false}
                showCheckIcon={true}
                ariaLabel="createaccount-confirm-password"
                descriptionClass="password-checks__description"
                errorLabelClass={`password-checks__errorlabel ${inlineLabel ? 'error-label' : ''}`}
                innerLabel={inlineLabel}
              />
            </div>
          </div>
        </div>
      </form>
    );
  };

  return (
    <StyledCreateAccountForm className="mb-3">
      {(!!errorsList?.length || (isOCJFlow && bannerMsgs?.key)) && (
        <Messages messageType="error-sev1" className="mt-3 account-page-error-msg mb-4 px-2">
          {errors?.prohibitedCountryErrMsg ? (
            <RichText text={prohibitedCountryErrMsg} componentId="error-span" />
          ) : errors?.uxlFailureErrorMessage ? (
            <RichText text={pageContext?.uxlErrorMessage} componentId={'uxl-failure-msg'} />
          ) : bannerMsgs?.key ? (
            <RichText
              text={
                bannerMsgs?.key === ONE_CLICK_JOIN_ERROR.EMAIL_DUPLICATE_ERROR
                  ? emailDuplicateErrMsg
                  : pageContext?.uxlErrorMessage
              }
              componentId="error-span"
            />
          ) : isValidPartnerPortal && errors?.existingAccountInlineErrorMessage ? (
            <>
              <span>{errorMessageHeader}</span>
              <RichText
                text={
                  handleFallback(
                    partnerPortalObject?.existingBonvoyAccErrorMsg,
                    partnerPortalObject?.existingAccountErrorMessage
                  )?.replace('return_to_url', goBackUrl) ?? ''
                }
                componentId={'partner-portal-error'}
              />
            </>
          ) : (
            <>
              <span>{errorMessageHeader}</span>
              <ul>
                {errorsList.map((error, index) => (
                  <li key={index}>
                    <RichText text={error} componentId={'create-account-form-error-' + index} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </Messages>
      )}

      {!partnerPortalObject?.hideActivateAccountCta && (
        <div className={clsx('create-account__link row col-12 mt-5 mb-2 ', !eaa && 'flex flex-md-row flex-column')}>
          {eaa && isKoreanLocale ? (
            <RichText
              text={
                domRendered && memberSignInLabel
                  ? transformAnchorAttributes(memberSignInLabel, {
                      href: `${activateOnlineAccountPath}${queryParams}`,
                      class: 'm-link-action',
                    })
                  : ''
              }
              customClass="t-font-m sign-in-ko"
              componentId="inlinetextmsg"
            />
          ) : (
            <>
              <Text
                customClass={clsx(' pb-1 pb-md-0')}
                copyText={alreadyAMemberText}
                fontSize={Types.size.medium}
                element={Types.tags.paragraph}
              />
              <Link
                text={activateOnlineAccountText}
                linkClassName={clsx('m-link-action link-underline', eaa ? 'my-md-0 my-1 mx-1' : 'mx-md-1')}
                linkHref={
                  queryParams && eaa ? `${activateOnlineAccountPath}${queryParams}` || '' : activateOnlineAccountPath
                }
                target="_self"
                linkType="internal"
                custom_click_track_value={
                  eaa
                    ? `${getCustomClickTrackingPageName(undefined, router?.pathname)}|${
                        TRACKING_CONSTANTS.SIGN_IN_TRACK
                      }|${TRACKING_CONSTANTS.INTERNAL}|`
                    : CREATE_ACCOUNT_CLICK_VAL
                }
              />
            </>
          )}
        </div>
      )}

      {eaa ? renderEAAForm() : renderForm()}
      {!eaa && (
        <div className="row col-12 mb-2">
          <CheckBox
            onChange={handleCheckboxChange}
            checked={remembermeCheckBox}
            checkboxName="remember_me"
            children={
              <RichText
                customClass="t-font-m paragraph-margin px-2"
                text={rememberMeLabel}
                componentId={`checkbox-${rememberMeLabel}`}
              />
            }
            checkboxId={inputId}
            className="create_account__checkbox"
          />
        </div>
      )}
    </StyledCreateAccountForm>
  );
};
